
import {LabelManager} from "./labels/LabelManager.js";
import {hoverEnter, hoverLeave, clearHover, getHoverTarget, hasPrimaryTarget, isHoverFrozen} from "./labels/hoverState.js";
import * as THREE from "../../libs/three.js/build/three.module.js";
import {Utils} from "../utils.js";
import {EventDispatcher} from "../EventDispatcher.js";
import {FeatureManager} from "./FeatureManager.js";
import {FeatureVisual} from "./FeatureVisual.js";
import {PipeVisual} from "./PipeVisual.js";
import {createFeature} from "./Feature.js";
import {copyClickGuard} from "./CopyFeedback.js";
import {DIGITIZATION_UI_ATTR} from "./DialogHelpers.js";
import {createCoordinateInfoBox} from "./CoordinateInfoBox.js";
import {CursorPointMarker} from "./CursorPointMarker.js";
import {RubberBand} from "./RubberBand.js";
import {CursorTracker} from "./CursorTracker.js";
import {projectToScreen, closestTOnScreenSegment} from "./geometry/polylineGeometry.js";
import {findNearestPointCloudPoint} from "./geometry/pointCloudSpatialSearch.js";
import {findCoincidentEndpointGroups} from "./geometry/topologyQa.js";
import {snapTargetPointsForFeature, normalizeSnapTolerancePx, nearestSnapCandidate} from "./snapTargets.js";
import {settings} from "./settings/DigitizationSettings.js";
import {setMeasureSnapEnabled} from "../utils/Measure.js";
import {BoxSelection} from "./BoxSelection.js";
import {showToast} from "./CopyFeedback.js";
import {GeometryWritebackQueue} from "./GeometryWriteback.js";
import {openVertexMenu, openVectorOperationsMenu, openFeatureMenu, openMultiSelectionMenu, showCrossSectionReportForFeature} from "./VectorEditingMenu.js";
import {isPlainRightClick, shouldExitToolOnRightClick} from "./idleRightClick.js";
import {getArcRanges, reconcileArcRanges} from "./geometry/arcRangeStore.js";
import {DigitizationTools, DigitizationCategories} from "./ToolRegistry.js";
import {runTopologyQaScan, scanTopology, zoomToTopologyFinding, closeTopologyGap, zoomToFeature as zoomToFeatureInViewer} from "./TopologyQaPanel.js";
import {FolderManager} from "./FolderManager.js";
import {ExportRegistry} from "./export/ExportRegistry.js";
import {buildExportModel, runExport, previewExportFilename} from "./export/exportFacade.js";
import {mountDigitizationHelpPanel} from "./HelpPanel.js";
import {mountCsvProfileEditorPanel} from "../viewer/CsvProfileEditorPanel.js";
import {getKeyboardShortcutGroups} from "./help/keyboardShortcuts.js";
import {HELP_CATEGORIES, HELP_ARTICLES, getArticle, articlesInCategory, categoryLabel, searchArticles} from "./help/helpModel.js";
import {mountDigitizationSettingsPanel} from "./settings/SettingsPanel.js";
import {visibleSettingsGroups, settingsInGroup} from "./settings/SettingsSchema.js";
import {parametricShapeSurvives, PARAMETRIC_SHAPE_NAMESPACES} from "./geometry/parametricShapeStore.js";
import {shouldIgnoreGlobalShortcut} from "../utils/keyboardGuard.js";
import {ensureHudStyle, registerHudSlot, layoutHudStack} from "./HudSkin.js";
import {HudStatus} from "./HudStatus.js";
import {registerNativeClip as mirrorNativeClip, getNativeClip as lookupNativeClip} from "./nativeClip.js";
import {registerNativeSnit as mirrorNativeSnit, getNativeSnit as lookupNativeSnit} from "./nativeSnit.js";
import {registerNativeDeviation as mirrorNativeDeviation, removeNativeDeviation as dropNativeDeviation, getNativeDeviation as lookupNativeDeviation} from "./nativeDeviation.js";
import {registerLayerObject as mirrorLayerObject, unregisterLayerObject as dropLayerObject, notifyLayerObjectChanged as notifyLayerObject, hasLayerObject as lookupLayerObject} from "./layerObjects.js";
import {registerGraveReport as mirrorGraveReport, getGraveReport as lookupGraveReport, findGraveReport as lookupGraveReportByCloud} from "./nativeGraveReport.js";
import {registerNativeProfile as mirrorNativeProfile, getNativeProfile as lookupNativeProfile} from "./nativeProfile.js";

import {t} from "./i18n/index.js";
// Standardværktøj pr. geometritype for features indlæst fra en ekstern kilde (addFeaturesFromData): bestemmer kind/ikon/visning.
const LOADED_DEFAULT_TOOL = {Point: "digitize_point", LineString: "digitize_line", Polygon: "digitize_polygon"};

// Owns the digitization feature store, the interactive THREE.Scene that holds
// every FeatureVisual, and the generic capture ("insertion") state machine
// used by every registered tool (Point/Line/Polygon/Midpoint/...).
//
// Capture model: a tool stays active once selected. Left-click places a
// vertex at the current point-cloud pick; for Line/Polygon (maxPoints:
// Infinity) further left-clicks add more vertices and a right-click finishes
// WITHOUT placing a vertex at the click location; for Point/Midpoint
// (maxPoints: 1/2) the tool finishes automatically once that many vertices
// are placed. Either way, finishing immediately re-arms the same tool so the
// user can keep digitizing without reselecting it - stopInsertion() (or
// picking a different tool) is what actually deactivates it.
export class DigitizationModule extends EventDispatcher {
	constructor(viewer){
		super();

		this.viewer = viewer;
		this.renderer = viewer.renderer;

		this.featureManager = new FeatureManager();
		// Brugerdefinerede mapper (Registreringer): organisations-metadata om
		// registreringerne, ikke features - se FolderManager.js.
		this.folderManager = new FolderManager(this.featureManager);

		this.scene = new THREE.Scene();
		this.scene.name = "scene_digitization";
		this.light = new THREE.PointLight(0xffffff, 1.0);
		this.scene.add(this.light);

		this.viewer.inputHandler.registerInteractiveScene(this.scene);

		// featureId -> FeatureVisual. Kept here (not on Feature/FeatureManager)
		// so the visual/interactive layer and the registered data stay
		// logically separate.
		this.visuals = new Map();
		this.selectedVisual = null;
		// Box-selection result (see BoxSelection.js) - entirely separate from
		// selectedVisual (which stays the single-whole-feature legacy
		// selection): null, or {scope: "feature"|"vertex", entries: [...]}.
		// Never mixed scopes at once. Any geometry-changing operation clears
		// this unconditionally (see clearMultiSelection()'s own doc comment) -
		// the simplest rule that can never leave a stale vertex/feature
		// reference behind after Split/Merge/Trim/delete/etc.
		this.multiSelection = null;
		this.activeTool = null;
		// Operation-lifecycle (se idleRightClick.js): false = intet aktivt værktøj, ELLER aktivt værktøj i idle (armeret, ingen
		// registrering/måling påbegyndt). true = værktøjets workflow er i gang (første klik/valg er sket) og forbliver det
		// gennem alle efterfølgende faser og dialoger, til værktøjet deaktiveres (stopInsertion/cancelActiveTool) eller genarmes
		// (startInsertion - et værktøj der efter en færdig registrering fortsætter, er dermed idle igen). Capture-primitiverne
		// sætter det via _markOperationStarted().
		this._operationActive = false;
		// Åbne værktøjsdialoger: en prompt FØR første capture (Vinkelret/Parallelforskydning: afstandsprompten) er ikke modal over
		// canvas'et (værts-dialogen ligger i hjørnet uden dæmpning), så højreklik når stadig canvas'et mens den er åben - den
		// tæller derfor som "operation i gang", så længe den er åben (DOM-overlays spores via isConnected, værts-dialoger via tæller).
		this._openToolDialogs = new Set();
		this._openHostDialogs = 0;

		// Id for den punktsky/model der er "aktiv" i Vue-toolbarens
		// modelkontrol lige nu - sat udefra via setActivePointcloud(), laest
		// (kun) i registerNewFeature() for at stemple nye registreringer med
		// deres model-tilhoerighed. null indtil Vue-siden har sat den foerste
		// gang (fx foer noget er indlaest).
		this.activePointcloudId = null;

		// Modellen en IGANGVÆRENDE værktøjsoperation (første klik/valg sket, se _operationActive) blev påbegyndt under - fastfrosset i
		// det øjeblik brugeren skifter aktiv model MIDT i operationen (setActivePointcloud()), så registreringen får den model, den
		// blev tegnet på, og ikke den nye. undefined = intet fastfrosset: den levende this.activePointcloudId gælder (uændret
		// adfærd, så længe modellen ikke skiftes midt i en operation). Ryddes ved (gen)armering, stop og ESC.
		this._operationPointcloudId = undefined;

		// Registreringsprofil (trin B1): opakt profil-objekt sat af hosten for NYE registreringer, se
		// setRegistrationProfile()/commitCandidates(). null = PointView standard (ren synkron pass-through).
		this.registrationProfile = null;
		// Igangværende profil-finaliseringer ({tool, controller, aborted}); tom i PointView standard. Se _finalizeCandidates().
		this._finalizations = new Set();
		// Frosset capture-kontekst ({pointcloudId}) mens en finaliseret kandidat registreres; se _wireFeatureLifecycle().
		this._commitContext = null;
		// Opake integrations-adaptere (trin F): {<domainData-navnerum>: adapter}, sat af hosten. En Feature med en binding i
		// `domainData[navnerum]` styres af det navnerums adapter - IKKE af den aktive Registreringsprofil. Se setIntegrationAdapters().
		this.integrationAdapters = {};
		// Features hvis bruger-initierede sletning afventer en adapter (forhindrer dobbelt-sletning).
		this._pendingRemovals = new Set();
		// Geometri-writeback (trin H): en afsluttet bruger-ændring af en bundet Features geometri sendes til adapterens
		// persistGeometry() (kun hvis den ikke er geometri-låst); ved fejl rulles den lokale geometri tilbage.
		this.geometryWriteback = new GeometryWritebackQueue({
			getFeature: (id) => this.featureManager.get(id) || null,
			resolve: (feature) => this._resolveIntegration(feature),
			isLocked: (feature) => this.getEditingLocks(feature).geometryLocked,
			restore: (id, geometry) => this.setFeatureGeometryFromData(id, geometry),
			notify: (message) => showToast(message, 5000),
			isInteracting: () => !!(this.viewer && this.viewer.inputHandler && this.viewer.inputHandler.drag)
		});
		// Integrations-laase (trin G1) synkroniseres på visualen, når en Feature tilføjes/ændres (binding kan komme til).
		this.featureManager.addEventListener("feature_added", (e) => this._syncEditLock(e.feature));
		this.featureManager.addEventListener("feature_changed", (e) => this._syncEditLock(e.feature));

		// general snap-target registry: every finished feature's vertices
		// (see snapTargets.js - every real vertex of a LineString/Polygon,
		// the point itself for a Point) register here so any tool's point
		// placement can snap to them, including a node a topology operation
		// inserted mid-line - see _pickPlacementPosition()/findSnapTarget()
		this.snapTargets = [];

		// Global interaction settings (Snap on/off, surface detection) are
		// forwarded to the engines that do not read the settings store
		// themselves - see applyInteractionSettings().
		this.applyInteractionSettings();
		settings.onChange((key) => {
			if(key === "*" || key === "interaction" || String(key).startsWith("interaction.")){
				this.applyInteractionSettings();
			}
		});

		// Instruction overlay: shared across every tool, top-center of the
		// viewer. A tool only ever supplies WHAT to show (name + per-state
		// instruction text via tool.instructions - see _resolveInstruction());
		// this box owns HOW it's shown (compact single-line layout, staying up
		// for the whole operation instead of vanishing after the first click).
		// No ESC hint here by design - ESC still cancels, it's just not
		// spelled out in the box (kept deliberately small/unobtrusive).
		this.hintElement = document.createElement('div');
		// Fælles HUD-skin (HudSkin.js): mørk, halvgennemsigtig, hvid tekst - læsbar mod en travl punktsky. Placeres top-centreret
		// over det faktiske viewer-område (se _showInstruction()), så sidebar/topbar ikke forskyder den ud af viewerens midte.
		ensureHudStyle();
		this.hintElement.className = 'pvh pvh--top pvh-hint';
		this.hintElement.style.cssText = 'z-index: 10000; display: none;';
		registerHudSlot('instruction', this.hintElement);
		window.addEventListener('resize', () => layoutHudStack());

		this.hintTitleEl = document.createElement('span');
		this.hintTitleEl.style.cssText = 'font-weight: bold; opacity: 0.85;';

		const hintSeparator = document.createElement('span');
		hintSeparator.style.cssText = 'opacity: 0.6; margin: 0 5px;';
		hintSeparator.textContent = '·';

		this.hintTextEl = document.createElement('span');
		this.hintTextEl.style.cssText = 'font-weight: bold;';

		this.hintElement.append(this.hintTitleEl, hintSeparator, this.hintTextEl);
		document.body.appendChild(this.hintElement);
		// viewerens statusplads i HUD-stakken (under instruktion og kort bekræftelse); værten (frontendens beskedkerne) tegner i den
		// via viewer.hudStatus, og den ældre viewer.postMessage-liste bor i samme plads
		if(this.viewer.hudStatus) this.viewer.hudStatus.dispose(); // modulet kan instantieres igen for samme viewer: én statusplads pr. viewer
		this.hudStatus = new HudStatus(this.viewer);
		this.viewer.hudStatus = this.hudStatus;

		// Chromes (og enhver anden browsers) native højreklik-menu må heller
		// ikke vises inde i Digitaliserings EGNE popups - dialoger
		// (createDialogOverlay()), Shift+højreklik-menuerne (VertexContextMenu.js)
		// og resultatpaneler (TopologyQaPanel.js/VolumeResultPanel.js/...).
		// InputHandler.js's egen contextmenu-blokering dækker kun
		// viewer.renderArea, og disse popups er bevidst siblings til den
		// (appended til document.body, ikke renderArea) - se hver fils egen
		// markDigitizationUI()-kald. ÉN capturing lytter her dækker dem alle,
		// uanset hvilken fil der byggede popup'en, uden at document.body
		// blokeres generelt (host-appens egne felter/dialoger uden for
		// Digitalisering er upåvirkede). preventDefault() virker ens i alle
		// browsere - det er ikke en Chrome-specifik fix.
		document.addEventListener("contextmenu", (e) => {
			if(e.target.closest(`[${DIGITIZATION_UI_ATTR}]`)) e.preventDefault();
		}, true);

		// Global navigation override: holding Ctrl suspends every capture/
		// preview handler below (they all check isPaused() first) so the
		// point cloud can be freely orbited/panned/zoomed without placing or
		// dragging a vertex, without cancelling whatever capture is in
		// progress - releasing Ctrl resumes exactly where it left off. The
		// blur listener is a failsafe: if focus leaves the window while Ctrl
		// is held (e.g. alt-tab), no keyup ever fires and digitization would
		// otherwise stay stuck paused.
		this.ctrlHeld = false;
		window.addEventListener("keydown", (e) => { if(e.key === "Control") this.ctrlHeld = true; });
		window.addEventListener("keyup", (e) => { if(e.key === "Control") this.ctrlHeld = false; });
		window.addEventListener("blur", () => { this.ctrlHeld = false; });

		// Rectangle/box-selection (Ctrl+Right-click+drag) - see BoxSelection.js's
		// own header comment for the full gesture-ownership mechanism. Registers
		// itself with the viewer's InputHandler in its own constructor.
		this.boxSelect = new BoxSelection(this);

		// Global hard-cancel: Escape discards the active tool's entire
		// in-progress capture (no partial save, unlike switching tools - see
		// cancelActiveTool()) and deactivates it, aborts any pending
		// interactive editing flow, and clears every selection scope. Skipped
		// while the user is typing in a text field (e.g. editing a feature's
		// name/description) so Escape-to-cancel-editing-that-field doesn't
		// also kill the tool.
		//
		// UI-adfærd task §7: ESC has ONE consistent priority order platform-
		// wide - (1) an active dialog/input-operation, (2) an active geometry/
		// tool operation, (3) an open SideRail panel, (4) otherwise nothing.
		// Level 1 never actually reaches this handler at all: every popup
		// dialog in VectorEditingMenu.js (Fast vinkel, Flyt punkt i Z, Bue fra
		// pilhøjde, ...) owns a keydown listener on its own box that calls
		// `e.stopPropagation()` on Escape (and DistancePrompt.js/
		// WallThicknessPrompt.js already do the same for their own dialogs) -
		// bubbling never gets this far while one is open, so this handler only
		// ever sees level 2 and below. `hadActiveOperation` below is captured
		// BEFORE any of the level-2 cancellation side-effects run (they would
		// otherwise erase the very state used to detect "was there something
		// to cancel"): `this.hintElement`'s visibility is the single, already-
		// existing signal EVERY level-2 flow shares - an armed capture tool
		// AND every ad-hoc interactive picking flow (reference-line picking,
		// Flyt punkt, Vælg sammenhængende del, Omtegn del, ...) show their own
		// instruction text via `_showInstruction()`/hide it via
		// `_hideToolHint()` on start/finish - so "the hint bar is visible"
		// reliably means "there is a more specific operation to cancel first",
		// without needing a second, parallel flag threaded through every one
		// of those flows.
		window.addEventListener("keydown", (e) => {
			if(e.key !== "Escape") return;
			clearHover("escape");

			const active = document.activeElement;
			const isEditingText = active && (active.tagName === "INPUT" || active.tagName === "TEXTAREA" || active.isContentEditable);
			if(isEditingText) return;

			// Cancelling an in-progress box-selection drag must not ALSO
			// deactivate the user's currently-armed tool OR close the SideRail
			// panel as an unrelated side effect - see
			// BoxSelection.cancelIfActive()'s own doc comment.
			if(this.boxSelect.cancelIfActive()) return;

			// Captured BEFORE cancellation - see this handler's own doc comment.
			const hadActiveOperation = this.hintElement.style.display !== 'none' || !!this.activeTool || !!this.multiSelection;

			// (selve udgangen er delt med højreklik-i-idle-reglen - se _exitActiveToolLikeEscape())
			this._exitActiveToolLikeEscape();

			if(hadActiveOperation) return; // level 2 handled this keystroke

			// Level 3: nothing more specific was active - collapse an open
			// SideRail panel, reusing its own existing open/close state
			// (SideRail.js's `openId`/`closePanel()`) rather than tracking a
			// second, parallel "is a panel open" flag here.
			if(this.viewer.sideRail && this.viewer.sideRail.openId){
				this.viewer.sideRail.closePanel();
			}
		});

		// Global label-visibility keyboard shortcuts (task §17) - 'L' toggles
		// ORDINARY labels, 'Shift+L' toggles RESULT labels, for every
		// currently-registered visual at once. FeatureVisual AND PipeVisual
		// both expose the exact same labelsVisible/setLabelsVisible/
		// resultLabelVisible/setResultLabelVisible/hasResultLabel() contract
		// (see PipeVisual.js's own doc comment for why a Rør's result label is
		// deliberately aliased to its one shared label) - this shortcut reuses
		// that authoritative model directly rather than hardcoding feature
		// types or inventing a parallel global label-visibility flag. Same
		// aggregate-then-invert rule ("everything relevant is currently ON ->
		// turn it all OFF, otherwise turn it all ON") as
		// RegistreringerPanel.js's own tri-state checkboxes
		// (getLabelCheckState()/getResultLabelCheckState()) use per node -
		// applied here across every visual at once, so a single keypress
		// always lands in a fully-ON or fully-OFF state, never a partial one.
		// Guarded against text-entry via the shared utils/keyboardGuard.js
		// (the same guard the classification quick-toggle shortcuts 1-4 use -
		// see ClassificationQuickToggles.js - extracted here so both share
		// ONE definition of "is the user currently typing somewhere"), and
		// against Ctrl/Alt/Meta so browser/OS shortcuts that happen to use
		// the L key are left alone. Dispatches "label_visibility_changed" so a
		// currently-open Registreringer panel can refresh its own checkboxes
		// to match (see RegistreringerPanel.js's own listener) - the one
		// thing a purely local per-visual toggle could not otherwise keep in
		// sync, since label visibility is FeatureVisual/PipeVisual-instance
		// state, not something the panel's tree already re-renders from on
		// every change.
		window.addEventListener("keydown", (e) => {
			if(e.key.toLowerCase() !== "l" || e.ctrlKey || e.altKey || e.metaKey) return;
			if(e.repeat) return; // en nedholdt tast skifter ikke tilstand igen og igen
			if(shouldIgnoreGlobalShortcut()) return;

			const wantsResultLabel = e.shiftKey;
			// Task "fuld ensretning": native Potree measurement/volume tools
			// (viewer.scene.measurements/volumes) now implement the exact same
			// labelsVisible/setLabelsVisible/resultLabelVisible/
			// setResultLabelVisible/hasResultLabel contract as FeatureVisual.js/
			// PipeVisual.js (see Measure.js/Volume.js) - gathered here alongside
			// this.visuals so ONE shortcut covers every label in the viewer, not
			// just Digitalisering's own. Read live (not cached) since these
			// arrays are mutated in place by Scene.js as measurements/volumes
			// are added/removed.
			const candidates = [
				...this.visuals.values(),
				...this.viewer.scene.measurements,
				...this.viewer.scene.volumes,
				// objects registered by the host app (e.g. the Vue "GCP'er" layer) that implement the same
				// label contract, so L covers their labels too without a separate handler/state
				...(this.viewer.externalLabelVisuals || []),
			].filter((v) => typeof v.setLabelsVisible === "function");
			// Shift+L styrer resultat-labels; L styrer de øvrige labels og rører ikke et objekt, hvis eneste label ER resultatet (rør: labelsAreResultOnly)
			const relevant = wantsResultLabel
				? candidates.filter((v) => v.hasResultLabel && v.hasResultLabel())
				: candidates.filter((v) => !v.labelsAreResultOnly);
			if(relevant.length === 0) return;

			const allOn = relevant.every((v) => (wantsResultLabel ? v.resultLabelVisible : v.labelsVisible));
			const newState = !allOn;
			for(const visual of relevant){
				if(wantsResultLabel) visual.setResultLabelVisible(newState);
				else visual.setLabelsVisible(newState);
			}

			this.dispatchEvent({type: "label_visibility_changed", resultLabel: wantsResultLabel});
		});

		// Live canvas-relative cursor position, tracked independently of any
		// tool's own short-lived capture listeners, so features can reveal
		// their segment-midpoint "insert vertex" handles on simple proximity
		// hover at any time - not just while a capture tool is active.
		this.mouse = null;
		const domElement = this.viewer.renderer.domElement;

		// Permanent coordinate readout, bottom-left of the viewer (mounted
		// into renderArea, a sibling of the canvas - not the sidebar, so it's
		// never inside a scrollable area). Updated from the same mousemove
		// event as `this.mouse` above rather than a separate listener or a
		// per-rendered-frame query - one point-cloud pick per physical mouse
		// movement, the same cost every tool's own live preview already pays.
		this.coordinateInfoBox = createCoordinateInfoBox(this.viewer.renderArea);

		// "C" - kopiér koordinatboksens aktuelle X/Y/Z til udklipsholderen.
		// Kun når der er en indlæst punktsky OG boksen viser et reelt pick
		// (copyCurrent() selv tjekker det sidste og returnerer false for den
		// tomme "—"-tilstand, så der ikke skrives "X: —"-tekst til
		// udklipsholderen). Samme globale-genvej-vagt som L/Shift+L/1-4, så
		// "c" i et tal- eller tekstfelt (fx et objekts navn) aldrig kapres.
		window.addEventListener("keydown", (e) => {
			if(e.key.toLowerCase() !== "c" || e.ctrlKey || e.altKey || e.metaKey) return;
			if(shouldIgnoreGlobalShortcut()) return;
			if(this.viewer.scene.pointclouds.length === 0) return;

			this.coordinateInfoBox.copyCurrent();
		});

		// Perf (task: "navigationen foeles hakkende ift. raa Potree"):
		// getMousePointCloudIntersection() is a real octree raycast, not a
		// cheap lookup - firing it on every single mousemove (which can be
		// 60-100+ events/sec while the user is simply orbiting/panning) added
		// continuous raycast cost raw Potree never pays, since native Potree
		// only picks while a measuring/volume tool is actively capturing.
		// Two cheap guards restore that: skip entirely while any mouse
		// button is held (mid-drag/orbit, where the coordinate readout is
		// least useful anyway and smoothness matters most), and throttle the
		// remaining hover-only picks to at most ~10/sec - still visually
		// instant for a readout, nowhere near every mousemove event.
		// Den lille blå kugle ved cursoren (CursorPointMarker.js): vises for ALLE værktøjer, så længe et værktøj er aktivt, på det
		// punkt i punktskyen (eller snap-mål), et venstreklik vil placere. Positionen leveres af CursorTracker (flyttes hver frame; pick'et
		// er omkostnings-tilpasset og deles med koordinatboksen); uden aktivt værktøj kun koordinatboksens egne ~10 pick/s.
		this.cursorMarker = new CursorPointMarker();
		this.scene.add(this.cursorMarker);

		// Den dynamiske streg fra det sidst placerede punkt til cursoren (RubberBand.js) - sat af værktøjerne via setRubberBand().
		this.rubberBand = new RubberBand();
		this.scene.add(this.rubberBand);

		let lastCoordinatePickAt = 0;
		const COORDINATE_PICK_INTERVAL_MS = 100;
		this._lastCoordinateAt = 0;

		// Kuglen og stregen følger musen via CursorTracker (CursorTracker.js): punktet flyttes hver frame, selve punktsky-pick'et
		// kører omkostnings-tilpasset, og en gen-tegning bestilles ved ændring (viewet tegner kun, når det er markeret "dirty").
		// Koordinatboksen deler tracker'ens punkt, mens et værktøj er aktivt (ét pick i stedet for to).
		this.cursorTracker = new CursorTracker(this, (position) => this._onCursorPosition(position));
		domElement.addEventListener("mousemove", (e) => {
			this.mouse = this._toCanvasMouse(e);

			// mid-træk/orbit: ingen pick, og kuglen skjules (den ville ellers hænge fast, mens kameraet bevæger sig)
			if(e.buttons !== 0){
				this.cursorTracker.stop();
				return;
			}

			if(this.activeTool && !this.isPaused()){
				this.cursorTracker.moved(e);
				return;
			}
			this.cursorTracker.stop();

			// intet aktivt værktøj: kun koordinatboksen, ~10 pick/s
			const now = performance.now();
			if(now - lastCoordinatePickAt < COORDINATE_PICK_INTERVAL_MS) return;
			lastCoordinatePickAt = now;
			this.coordinateInfoBox.update(this._pickPointCloudPosition(e));
		});
		domElement.addEventListener("mouseleave", () => {
			this.mouse = null;
			this.coordinateInfoBox.update(null);
			this.cursorTracker.stop();
		});

		// Per-vertex actions: hovering a vertex (tracked via each
		// FeatureVisual sphere's own mouseover/mouseleave - see
		// FeatureVisual._wireSphere()) and Shift+Right-clicking it opens a
		// small popup near the cursor (see VertexContextMenu.js).
		//
		// Deliberately NOT gated on "no active tool": a tool stays armed
		// indefinitely once selected (see this class's own doc comment - it
		// re-arms itself after every completed capture), so `this.activeTool`
		// is non-null during essentially all normal digitizing sessions. An
		// earlier version gated this whole feature behind `!this.activeTool`,
		// which meant it only ever worked in the brief window right after
		// pressing Escape - effectively never in real use. That was the
		// actual root cause of the "menu never opens" regression, not a
		// wiring/propagation problem.
		//
		// Registered on the same domElement an active tool's own capture
		// listener also uses (e.g. Line/Polygon's or the "Vinkelret"/
		// "Parallelforskydning" family's right-click-to-finish) - since this
		// listener is added once, here in the constructor, it always runs
		// BEFORE any later-added per-capture listener (same-element,
		// same-phase listeners fire in registration order). To stop that
		// active tool's own listener from *also* treating this same
		// Shift+Right-click as its own right-click-to-finish, this only calls
		// stopImmediatePropagation() - which is the only thing that blocks a
		// later listener on the same element - when it actually found a
		// hovered vertex and is about to show the menu; a Shift+Right-click
		// that doesn't land on a vertex is left untouched, so an active
		// tool's own right-click semantics are unaffected.
		// Single, deterministic hit-priority chain for the whole module:
		// vertex, then segment, then (only if neither hit) the currently
		// selected feature - never more than one of these three menus opens
		// per click, and each is tried via exactly one hit-test call (no menu
		// re-derives its own competing hit-test). Each open*() call below
		// returns false when the resolved selection has zero applicable
		// operations (see EditingCapabilities.js) - in that case no menu is
		// shown and the event is left alone (section 12's "no menu is better
		// than an empty one", and section 1's "no Digitalisering hit -> don't
		// touch the event").
		// Almindeligt højreklik i idle forlader værktøjet (som ESC): aktivt værktøj, ingen operation i gang, ikke Shift/Ctrl og
		// ikke et højre-træk (pan). Højreklik UNDER en operation er uberørt og går videre til værktøjets egen handler.
		// Registreres her i konstruktøren, så den kører FØR enhver per-capture-lytter (samme element, registreringsrækkefølge) -
		// stopImmediatePropagation() hindrer, at et idle højreklik ogsaa bliver til værktøjets eget "afslut"/re-arm.
		let rightDown = null;
		domElement.addEventListener("mousedown", (e) => {
			if(e.button === THREE.MOUSE.RIGHT){
				rightDown = {x: e.clientX, y: e.clientY, shiftKey: e.shiftKey, ctrlKey: e.ctrlKey};
			}
		}, true);
		domElement.addEventListener("mouseup", (e) => {
			if(e.button !== THREE.MOUSE.RIGHT) return;
			const down = rightDown;
			rightDown = null;
			const plain = isPlainRightClick({
				down,
				up: {x: e.clientX, y: e.clientY},
				shiftKey: e.shiftKey || !!(down && down.shiftKey),
				ctrlKey: e.ctrlKey || !!(down && down.ctrlKey),
				ctrlHeld: this.isPaused(),
			});
			if(!shouldExitToolOnRightClick({activeTool: this.activeTool, operationActive: this.isOperationActive(), plainRightClick: plain})) return;
			e.stopImmediatePropagation();
			this._exitActiveToolLikeEscape();
		}, false);

		domElement.addEventListener("mouseup", (e) => {
			if(e.button !== THREE.MOUSE.RIGHT || !e.shiftKey) return;

			// A box-selected multi-set (see BoxSelection.js) takes priority
			// over the ordinary vertex→segment→fill→whole-object chain below:
			// while 2+ elements are box-selected, that IS the user's current,
			// deliberate selection - drilling into a single element under the
			// cursor would silently abandon it. Predictable and simple: the
			// multi-selection menu always wins here for as long as it exists;
			// it only ever exists with 2+ entries (a single-result box-select
			// already collapses to the ordinary selectVisual() path - see
			// BoxSelection._finish()).
			if(this.multiSelection){
				if(openMultiSelectionMenu(this, e.clientX, e.clientY)) e.stopImmediatePropagation();
				return;
			}

			const vertexHit = this._findHoveredVertex();
			if(vertexHit){
				if(openVertexMenu(this, vertexHit.visual, vertexHit.index, e.clientX, e.clientY)) e.stopImmediatePropagation();
				return;
			}

			// No vertex under the cursor - fall back to a segment hit (see
			// _findHoveredSegment()'s own doc comment: vertex-hit always
			// takes priority, per section 1's explicit requirement, so this
			// branch is only ever reached once the vertex check has already
			// come back empty). Only LineString/Polygon features have
			// segments to hit at all.
			const segmentHit = this._findHoveredSegment(e);
			if(segmentHit){
				if(openVectorOperationsMenu(this, segmentHit.visual, segmentHit, e.clientX, e.clientY)) e.stopImmediatePropagation();
				return;
			}

			// A click inside a Polygon's fill (not near any edge - see
			// _findHoveredFill()) selects that feature on the spot and shows
			// its whole-feature menu directly, in one gesture - the same
			// "no pre-selection required" ergonomics the vertex/segment
			// branches above already have, rather than only working via a
			// separate plain-click-to-select first.
			const fillHit = this._findHoveredFill(e);
			if(fillHit){
				this.selectVisual(fillHit.visual);
				if(openFeatureMenu(this, fillHit.visual, e.clientX, e.clientY)) e.stopImmediatePropagation();
				return;
			}

			// Whole-object-only registrations (e.g. Rør - a PipeVisual has no
			// vertex/segment/fill mechanics of its own for the branches above
			// to hit-test) - same "select on the spot, then show its whole-
			// object menu" ergonomics the fill branch above already has.
			const wholeObjectHit = this._findHoveredWholeObject(e);
			if(wholeObjectHit){
				this.selectVisual(wholeObjectHit.visual);
				if(openFeatureMenu(this, wholeObjectHit.visual, e.clientX, e.clientY)) e.stopImmediatePropagation();
				return;
			}

			// Neither a vertex, segment, fill nor whole-object was hit
			// anywhere. Audit finding (context-menu revision): this used to
			// fall back to opening a menu for whatever feature happened to
			// still be `selectedVisual` from an earlier, unrelated click -
			// even on genuinely empty canvas. A professional GIS context
			// menu must relate to what was actually clicked, never a stale
			// prior selection - no Digitalisering hit here means the event
			// is left alone entirely, matching this file's own stated
			// principle above. A feature that's already selected can still
			// get its whole-object menu by Shift+Right-clicking it directly -
			// every branch above already covers that.
		});

		// Plain left-click selection (task: clicking a Linje/Polygon's own
		// body - a segment, or a Polygon's fill - must select it, exactly
		// like clicking a vertex sphere already does via its own 'click'
		// listener - see FeatureVisual._wireSphere()). Neither an edge
		// (Line2) nor fillMesh carries any event listener of its own, so
		// InputHandler's generic click mechanism never even raycast-tests
		// them (see InputHandler.getHoveredElements() - only nodes with a
		// registered listener are considered at all) - this reuses the same
		// robust, already-proven _findHoveredSegment()/_findHoveredFill()
		// hit-tests the Shift+Right-click menus above rely on, rather than
		// wiring new listeners onto Line2/Mesh objects whose raycasting
		// behaviour under InputHandler's generic mechanism is unproven here.
		// Deliberately skipped when a vertex is hovered - that case is
		// already fully handled by the sphere's own separate native 'click'
		// event (a different, later-firing DOM event than 'mouseup'), so
		// nothing here would add anything but a redundant call.
		domElement.addEventListener("mouseup", (e) => {
			if(e.button !== THREE.MOUSE.LEFT || e.shiftKey || this.isPaused() || this._consumeCopyClickGuard()) return;

			// A new plain single-element selection always supersedes an
			// active box-selected multi-set (task: "undgå at blande
			// forskellige selection scopes i én uklar selection state") -
			// cleared unconditionally here, before any hit-test, regardless
			// of whether this click actually lands on anything.
			if(this.multiSelection) this.clearMultiSelection();

			if(this._findHoveredVertex()) return;

			const segmentHit = this._findHoveredSegment(e);
			if(segmentHit){
				this.selectVisual(segmentHit.visual);
				return;
			}

			const fillHit = this._findHoveredFill(e);
			if(fillHit) this.selectVisual(fillHit.visual);
		});

		// Cursor reflects active-tool state for every tool uniformly, from
		// this one place - not per tool - by reacting to 'active_tool_changed'
		// (already dispatched by every lifecycle transition: startInsertion(),
		// stopInsertion(), cancelActiveTool()/ESC), rather than setting it
		// separately in each of those methods or in each tool's own capture
		// flow. A plain CSS cursor (crosshair) is enough to read clearly as
		// "registrering aktiv" without needing a custom cursor image.
		this.addEventListener('active_tool_changed', (e) => {
			this.viewer.renderer.domElement.style.cursor = e.tool ? "crosshair" : "";
			// ingen aktivt værktøj -> ingen kugle; et nyt værktøj viser den igen ved næste musebevægelse
			if(this.cursorTracker) this.cursorTracker.stop();
			if(this.cursorMarker) this.cursorMarker.hide();
			if(this.rubberBand) this.rubberBand.clear();
		});
		// ESC / værktøjsskift / afbrudt forløb: strengen hører til det forløb, der afbrydes
		viewer.addEventListener('cancel_insertions', () => { if(this.rubberBand) this.rubberBand.clear(); });

		this._wireSnitplan();

		this.labelManager = new LabelManager(this);
		this._initHover();
		viewer.addEventListener("update", this.update.bind(this));
		viewer.addEventListener("render.pass.perspective_overlay", this.render.bind(this));
	}

	isPaused(){
		return this.ctrlHeld;
	}

	// Den dynamiske streg (RubberBand.js): værktøjer kalder setRubberBand(punkt) efter hvert placeret punkt - stregen går så fra
	// punktet til cursoren, indtil clearRubberBand() (forløbet slutter) eller næste setRubberBand(). closeTo: valgfrit punkt,
	// som en lukkelinje fra cursoren også går til (polygonens første punkt).
	setRubberBand(anchor, {closeTo = null} = {}){
		this.rubberBand.set(anchor, closeTo);
		this._requestRedraw();
	}

	clearRubberBand(){
		this.rubberBand.clear();
		this._requestRedraw();
	}

	// Viewet tegner kun, når det er markeret "dirty": alt, der ændrer cursor-kuglen/stregen, bestiller en tegning.
	_requestRedraw(){
		const viewer = this.viewer;
		if(typeof viewer._requestRedraw === "function") viewer._requestRedraw(); else viewer.needsRedraw = true;
	}

	// CursorTrackers punkt (null = skjul): flytter kuglen og streg-enden; koordinatboksen opdateres højst ~10/s.
	_onCursorPosition(position){
		if(!position){
			this.cursorMarker.hide();
			this.rubberBand.updateCursor(null);
			return;
		}
		this.cursorMarker.show(position);
		this.cursorMarker.refreshScale(this.viewer.scene.getActiveCamera(), ...this._rendererSizeArgs());
		this.rubberBand.updateCursor(position);

		const now = performance.now();
		if(now - this._lastCoordinateAt >= 100){
			this._lastCoordinateAt = now;
			this.coordinateInfoBox.update(position.clone());
		}
	}

	// Sætter stregen ud fra en FeatureVisual under opbygning: fra sidste punkt (og for polygon, når der er mindst to punkter,
	// også lukkelinjen til det første punkt). `maxPoints`: når forløbet er fuldt, ryddes stregen.
	syncRubberBandToVisual(visual, maxPoints = Infinity){
		const pts = visual && visual.points;
		if(!pts || pts.length === 0 || pts.length >= maxPoints){
			this.rubberBand.clear();
			return;
		}
		const last = pts[pts.length - 1].position;
		const closeTo = visual.geometryType === "Polygon" && pts.length >= 2 ? pts[0].position : null;
		this.rubberBand.set(last, closeTo);
	}

	// Komplet liste over tastaturgenveje ([{id, title, note, items: [{keys, description}]}]) - samme data som hjælpeartiklen
	// "Keyboard shortcuts"; host-UI'et (Navigation-modalen) viser den, så listen ikke er hardcoded to steder.
	getKeyboardShortcuts(){
		return getKeyboardShortcutGroups();
	}

	// [bredde, højde] på rendereren (til ScreenSpaceScale)
	_rendererSizeArgs(){
		const size = this.renderer.getSize(new THREE.Vector2());
		return [size.width, size.height];
	}

	// A label's copy-icon click is consumed here so it never ALSO places a
	// digitization vertex at the same spot - see CopyFeedback.js for why the
	// guard is reliably set before this runs. Every raw 'mouseup' placement
	// handler must call this first and bail out if it returns true.
	_consumeCopyClickGuard(){
		if(!copyClickGuard.consumed) return false;
		copyClickGuard.consumed = false;
		return true;
	}

	_showInstruction(toolName, text){
		this.hintTitleEl.textContent = toolName;
		this.hintTextEl.textContent = text;
		this.hintElement.style.display = 'block';
		layoutHudStack();
	}

	_hideToolHint(){
		this.hintElement.style.display = 'none';
		layoutHudStack();
	}

	// Normalizes a tool's `instructions` field into the text for the current
	// state: a plain string (Point/Line/Polygon - one instruction for the
	// whole capture) or a function(pointCount) for tools whose text changes
	// as points are placed (e.g. Midpoint's "pick start" -> "pick end").
	// Tools with their own customCapture (e.g. Midline) don't go through
	// this - they pick a named key straight out of tool.instructions instead.
	_resolveInstruction(tool, pointCount){
		const instructions = tool.instructions;
		if(typeof instructions === "function") return instructions(pointCount);
		if(typeof instructions === "string") return instructions;
		return "";
	}

	// Hover som én funktion (labels/hoverState.js): vertex og indsæt-håndtag meldes af InputHandler via FeatureVisual; kant og helt objekt
	// (polygonens flade, rør) findes her med de eksisterende skærmrums-tests - højst ÉN test pr. frame, og kun når musen har flyttet sig.
	// Lærredets mouseleave, vinduet der mister fokus/skjules og værktøjsskift rydder målet (ingen efterladte labels).
	_initHover(){
		const dom = this.viewer.renderer.domElement;
		this._pointer = {clientX: 0, clientY: 0, dirty: false, buttons: 0};
		this._hoverTool = null;
		dom.addEventListener("mousemove", (e) => {
			const p = this._pointer;
			p.clientX = e.clientX; p.clientY = e.clientY; p.buttons = e.buttons; p.dirty = true;
		});
		dom.addEventListener("mouseleave", () => {
			this._pointer.dirty = false;
			clearHover("mouseleave");
		});
		window.addEventListener("blur", () => clearHover("blur"));
		document.addEventListener("visibilitychange", () => { if(document.hidden) clearHover("hidden"); });
	}

	_updateHover(now){
		if(this.activeTool !== this._hoverTool){
			this._hoverTool = this.activeTool;
			clearHover("tool");
		}
		const p = this._pointer;
		if(!p || !p.dirty) return;
		p.dirty = false;
		// ikke under værktøj, navigation med knap nede, træk, eller når et direkte træf (vertex/håndtag) allerede er hovermålet
		if(this.activeTool || p.buttons || this.visuals.size === 0 || isHoverFrozen() || hasPrimaryTarget()) return;
		const inputHandler = this.viewer.inputHandler;
		if(inputHandler && inputHandler.drag) return;

		const e = {clientX: p.clientX, clientY: p.clientY};
		const segment = this._findHoveredSegment(e, 10);
		if(segment){
			hoverEnter("segment", segment.visual, segment.segmentIndex, now, segment.distPx);
			return;
		}
		const fill = this._findHoveredFill(e) || this._findHoveredWholeObject(e);
		if(fill){
			hoverEnter("object", fill.visual, 0, now, 0);
			return;
		}
		const current = getHoverTarget();
		if(current && (current.kind === "segment" || current.kind === "object")){
			hoverLeave(current.kind, current.visual, current.index, now);
		}
	}

	_toCanvasMouse(e){
		const rect = this.viewer.renderer.domElement.getBoundingClientRect();
		return {x: e.clientX - rect.left, y: e.clientY - rect.top};
	}

	registerFeatureSnapTargets(feature){
		for(const p of snapTargetPointsForFeature(feature)){
			this.snapTargets.push({position: new THREE.Vector3(p[0], p[1], p[2]), featureId: feature.id});
		}
	}

	removeFeatureSnapTargets(featureId){
		this.snapTargets = this.snapTargets.filter(t => t.featureId !== featureId);
	}

	// Pushes the global interaction settings to the engines that have their own
	// flag instead of reading the settings store:
	//  - native Measure's snap (utils/Measure.js showSnap, a module-global flag)
	//    follows interaction.snapEnabled, so there is ONE Snap switch for the
	//    user. (Digitalisering's findSnapTarget reads the store directly; the
	//    old drawings follow via the frontend, which mirrors snapEnabled.)
	//  - viewer.detection (surface detection, InputHandler.surfaceDetect()).
	applyInteractionSettings(){
		setMeasureSnapEnabled(!!settings.get("interaction.snapEnabled"));
		this.viewer.detection = !!settings.get("interaction.surfaceDetection");
	}

	// Snap-maal under musen for Digitaliserings registrering/maaling. Styres af
	// de to Snap-indstillinger (settings/SettingsSchema.js, gruppe "interaction"):
	// `interaction.snapEnabled` slaar hele funktionen til/fra, `interaction.snapTolerancePx` er
	// afstanden i SKAERMPIXELS (se begrundelsen dér). Et eksplicit `maxPixels`
	// overstyrer indstillingen (ingen kalder bruger det i dag).
	//
	// Maal = hjoernepunkter paa eksisterende registreringer/maalinger
	// (snapTargets.js). Skjulte registreringer (visual.visible === false, sat
	// af Registreringer-trae'ets oje-ikon) er IKKE maal - man kan ikke se dem,
	// saa de maa ikke trække punkter til sig. Hjaelpegeometri (preview, aksler,
	// maalehjaelpere) er aldrig registreret som snap-maal. Eksplicitte handlinger
	// ("Snap til punktsky", "Snap til andet punkt") bruger IKKE denne metode og
	// er uafhaengige af indstillingen.
	findSnapTarget(canvasMouse, maxPixels){
		if(!settings.get("interaction.snapEnabled")) return null;
		if(maxPixels === undefined) maxPixels = normalizeSnapTolerancePx(settings.get("interaction.snapTolerancePx"));

		const camera = this.viewer.scene.getActiveCamera();
		const renderAreaSize = this.renderer.getSize(new THREE.Vector2());
		const width = renderAreaSize.width;
		const height = renderAreaSize.height;

		const items = [];
		const addItem = (position) => {
			const ndc = position.clone().project(camera);
			// bag kameraet projiceres et punkt spejlvendt ind paa skaermen
			if(ndc.z > 1) return;
			items.push({
				position,
				x: (ndc.x + 1) * width / 2,
				y: (-ndc.y + 1) * height / 2,
			});
		};
		for(const target of this.snapTargets){
			const visual = this.visuals.get(target.featureId);
			if(visual && visual.visible === false) continue;
			addItem(target.position);
		}
		// Explicit snap targets owned by the host app (e.g. the Vue "GCP'er" layer): viewer.externalSnapSources =
		// [{getSnapTargets(): [{x, y, z}]}]. Same tolerance, same nearest-wins rule and same switch as above - a
		// source only returns targets while its own layer is on.
		for(const source of this.viewer.externalSnapSources || []){
			for(const p of source.getSnapTargets()) addItem(new THREE.Vector3(p.x, p.y, p.z));
		}

		const best = nearestSnapCandidate(items, canvasMouse, maxPixels);
		return best ? best.position : null;
	}

	// raw point-cloud pick underneath the cursor, no snapping - "what the
	// cursor is actually pointing at right now" (used by the coordinate
	// infobox - see CoordinateInfoBox.js), as opposed to _pickPlacementPosition()
	// below, which is specifically for vertex *placement* and prefers a
	// nearby snap target over the raw pick.
	_pickPointCloudPosition(e){
		const mouse = this._toCanvasMouse(e);

		// With Overfladedetektion ON: the front-most local surface instead of the depth-blind nearest-pixel hit -
		// protects against pick-through. The SAME result (cached per mouse position) drives the disc, so the
		// registered position is the surface the disc shows. Falls back to the normal pick when no point is within
		// the small window (-> unchanged behaviour). OFF: the normal pick, unchanged.
		if(this.viewer.detection){
			const sd = this.viewer.inputHandler && this.viewer.inputHandler.surfaceDetection;
			if(sd){
				const r = sd.frontPick(mouse);
				if(r && r.ok) return r.location.clone();
			}
		}

		const I = Utils.getMousePointCloudIntersection(
			mouse,
			this.viewer.scene.getActiveCamera(),
			this.viewer,
			this.viewer.scene.pointclouds,
			{pickClipped: true});

		return I ? I.location : null;
	}

	// TRUE spatial nearest-neighbour pick around a given WORLD position -
	// "Snap til nærmeste punktsky punkt" (VectorEditingMenu.js) needs the
	// point-cloud point actually closest to the vertex in 3D, not whatever
	// a screen-ray happens to hit looking from the current camera (that
	// would silently miss a nearer point sitting just behind/below the
	// vertex along the current view direction, and would change answer as
	// the user orbits - user-reported bug, see findNearestPointCloudPoint()'s
	// own doc comment for how the spatial search itself works).
	_pickPointCloudPositionAt(worldPosition){
		return findNearestPointCloudPoint(this.viewer.scene.pointclouds, worldPosition);
	}

	// canvas-relative mouse -> a placement position: an existing snap target
	// if the mouse is close enough to one, otherwise the point-cloud pick
	// underneath the cursor. Used for every vertex placement so any tool can
	// snap to previously finished features.
	_pickPlacementPosition(e){
		const mouse = this._toCanvasMouse(e);

		const snapped = this.findSnapTarget(mouse);
		if(snapped) return snapped.clone();

		return this._pickPointCloudPosition(e);
	}

	// Captures a standalone construction polyline (left-click adds a vertex,
	// right-click finishes) that is never registered as a Feature itself -
	// used by tools like Midline that digitize helper geometry first. Labels
	// the first/last vertex "Start"/"Slut" as vertices are added.
	// onDone(visual|null) fires once, on finish or cancel.
	_captureVector(onDone){
		const domElement = this.viewer.renderer.domElement;
		const visual = new FeatureVisual("LineString");
		this.scene.add(visual);

		const updateRoleLabels = () => {
			const n = visual.points.length;
			const roles = new Array(n).fill("");
			if(n >= 1) roles[0] = t("Start");
			if(n >= 2) roles[n - 1] = t("Slut");
			visual.vertexRoleNames = roles;
			for(let i = 0; i < n; i++) visual._refreshVertexLabel(i);
		};

		let finished = false;

		const cleanup = () => {
			domElement.removeEventListener('mouseup', onMouseUp, false);
			this.viewer.removeEventListener('cancel_insertions', onCancel);
		};

		const finish = () => {
			if(finished) return;
			// For få punkter til en vektor: ignorér højreklikket og fortsæt registreringen (samme konvention som
			// Tværprofilrapport/Areal og volumen). Før lukkede det capture'en med onDone(null), og kalderne (Midterlinje,
			// Vinkelret, Parallelforskydning) returnerede stille - værktøjet stod aktivt uden nogen capture-handler.
			if(visual.points.length < 2) return;
			finished = true;
			cleanup();
			onDone(visual);
		};

		const onCancel = () => {
			if(finished) return;
			finished = true;
			cleanup();
			this.scene.remove(visual);
			onDone(null);
		};

		const onMouseUp = (e) => {
			if(finished || this.isPaused() || this._consumeCopyClickGuard()) return;

			if(e.button === THREE.MOUSE.LEFT){
				const location = this._pickPlacementPosition(e);
				if(location){
					visual.appendAtEnd(location);
					this._markOperationStarted();
					updateRoleLabels();
				}
			}else if(e.button === THREE.MOUSE.RIGHT){
				finish();
			}
		};

		domElement.addEventListener('mouseup', onMouseUp, false);
		this.viewer.addEventListener('cancel_insertions', onCancel);
	}

	// Selection is handled locally (not via InputHandler.toggleSelection,
	// which assumes a `.boundingBox` only Volume provides) so a vertex click
	// or a feature-list click both funnel through here.
	selectVisual(visual){
		if(this.selectedVisual === visual) return;

		if(this.selectedVisual) this.selectedVisual.setSelected(false);
		this.selectedVisual = visual || null;
		if(this.selectedVisual) this.selectedVisual.setSelected(true);

		this.dispatchEvent({type: "visual_selection_changed", visuals: this.selectedVisual ? [this.selectedVisual] : []});
	}

	// Tears down whatever box-selection result is currently active (see
	// BoxSelection.js) - restores every entry's highlight (feature-scope:
	// setSelected(false), unless that same visual also happens to be the
	// single selectedVisual, which keeps its normal orange; vertex-scope:
	// setVertexHighlight(index, false)) and nulls the field. Called
	// unconditionally from every geometry-changing commit path
	// (applyFeatureGeometry/registerNewFeature/removeFeature - see their own
	// comments) rather than trying to patch stale indices/references after
	// an edit - the simplest rule that can never leave multiSelection
	// pointing at a vertex index or feature that no longer matches reality.
	// Also called on ESC and the multi-selection menu's own "Ryd valgte".
	clearMultiSelection(){
		if(!this.multiSelection) return;

		const {scope, entries} = this.multiSelection;
		for(const entry of entries){
			if(scope === "feature"){
				if(entry.visual !== this.selectedVisual) entry.visual.setSelected(false);
			}else if(scope === "vertex"){
				if(entry.visual.setVertexHighlight) entry.visual.setVertexHighlight(entry.vertexIndex, false);
			}
		}

		this.multiSelection = null;
	}

	// Den native 2D-profil (Potrees ProfileTool) som en Registrering under Maalinger - se nativeProfile.js. Idempotent;
	// returnerer feature-id'et (null hvis profilen ikke er faerdig, dvs. under to punkter).
	registerNativeProfile(profile){
		return mirrorNativeProfile(this, profile);
	}

	// Den native profil bag en feature (null for alle andre registreringer).
	getNativeProfile(featureId){
		return lookupNativeProfile(this, featureId);
	}

	// Potrees Clip-objekter (volumen/polygon/skaermboks) som objekter under Maalinger - se nativeClip.js. kind: "volume" | "polygon".
	registerNativeClip(object, kind, label = null){
		return mirrorNativeClip(this, object, kind, label);
	}

	// {object, kind} bag en feature (null for alle andre registreringer).
	getNativeClip(featureId){
		return lookupNativeClip(this, featureId);
	}

	// Snitplaner (viewer.snitplan) som ÉT objekt pr. Snitplan under Maalinger - se nativeSnit.js.
	registerNativeSnit(volume){
		return mirrorNativeSnit(this, volume);
	}

	// {volume} bag en feature (null for alle andre registreringer).
	getNativeSnit(featureId){
		return lookupNativeSnit(this, featureId);
	}

	// Afvigelsesanalysen som ÉT objekt under Maalinger - se nativeDeviation.js.
	registerNativeDeviation(info){
		return mirrorNativeDeviation(this, info);
	}

	removeNativeDeviation(){
		return dropNativeDeviation(this);
	}

	getNativeDeviation(featureId){
		return lookupNativeDeviation(this, featureId);
	}

	// Hvert Snitplan (også de, der allerede findes og dem, der kommer til - oprettelse, indlaesning af opsaetning) faar sin
	// raekke i traeet; selve livscyklussen ejes af viewer.snitplan.
	_wireSnitplan(){
		const manager = this.viewer.snitplan;
		if(!manager) return;
		manager.addEventListener("snit_added", (e) => this.registerNativeSnit(e.snit));
		manager.list().forEach((volume) => this.registerNativeSnit(volume));
	}

	// Værtens lag (3D-baggrundskort, IFC/OBJ-model, GIS-lag) som objekter i Registreringer ("Data & Lag") med øje og slet - se
	// layerObjects.js for kontrakten.
	registerLayerObject(spec){
		return mirrorLayerObject(this, spec);
	}

	unregisterLayerObject(key){
		return dropLayerObject(this, key);
	}

	notifyLayerObjectChanged(key){
		return notifyLayerObject(this, key);
	}

	hasLayerObject(key){
		return lookupLayerObject(this, key);
	}

	// Graverapporten (automatisk udgravning/påfyldning) som varigt objekt under Målinger med 3D-visning - se nativeGraveReport.js.
	// info: {name, result}. Erstatter en eksisterende graverapport for samme punktsky. Returnerer feature-id'et.
	registerGraveReport(pointcloud, info){
		return mirrorGraveReport(this, pointcloud, info);
	}

	// {pointcloudId, name, result} bag en feature (null for alle andre registreringer).
	getGraveReport(featureId){
		return lookupGraveReport(this, featureId);
	}

	// Feature-id'et for en punktskys graverapport (null hvis ingen).
	findGraveReport(pointcloudId){
		return lookupGraveReportByCloud(this, pointcloudId);
	}

	// Åbner en Tværprofil-registrerings rapport igen (regenereres ud fra objektets aktuelle geometri) - fra Registreringer-træet.
	showCrossSectionReport(featureId){
		const visual = this.getVisual(featureId);
		if(visual) return showCrossSectionReportForFeature(this, visual);
	}

	getVisual(featureId){
		return this.visuals.get(featureId);
	}

	getFeature(visual){
		for(const [id, v] of this.visuals){
			if(v === visual) return this.featureManager.get(id);
		}
		return null;
	}

	// finds the vertex currently under hover (across every committed
	// feature's visual - see FeatureVisual's per-sphere userData.hovered),
	// for the Shift+Right-click vertex context menu. Returns
	// {visual, index} or null if nothing is hovered.
	//
	// Audit finding (context-menu revision): `this.visuals` can hold visuals
	// with no vertex mechanics at all (e.g. PipeVisual, a plain
	// THREE.Object3D with no `.spheres`) - this used to call
	// `.spheres.findIndex` unconditionally and crash with a TypeError the
	// moment ANY Shift+Right-click happened while such a feature existed
	// anywhere in the scene, regardless of what was actually clicked. The
	// `if(!visual.spheres) continue` guard below (and the matching one in
	// _findHoveredSegment()) is the fix - see _findHoveredWholeObject() for
	// how whole-object-only registrations get their own menu instead.
	_findHoveredVertex(){
		for(const visual of this.visuals.values()){
			if(!visual.spheres) continue;
			const index = visual.spheres.findIndex(s => s.userData.hovered);
			if(index !== -1) return {visual, index};
		}
		return null;
	}

	// Screen-space nearest-segment hit test across every committed feature's
	// visual (LineString/Polygon only - a Point has no segments) - the
	// Shift+Right-click-on-a-segment counterpart to _findHoveredVertex()
	// (see section 1: vertex-hit takes priority, so this is only ever
	// consulted once _findHoveredVertex() already came back empty). Reuses
	// the exact same screen-projection/closest-point-on-a-screen-segment
	// primitives Midterlinje's own connector already relies on
	// (geometry/polylineGeometry.js), rather than a second, parallel
	// distance metric. `maxPixels` matches findSnapTarget()'s own default
	// hit radius, for a consistent "how close counts as a hit" feel across
	// the module. Returns {visual, segmentIndex, t, point} - `point` is the
	// actual 3D position (Z interpolated) under the cursor on that segment,
	// used both to position the popup's implied location and as the split
	// point for "Indsæt punkt" - or null if nothing is close enough.
	_findHoveredSegment(e, maxPixels = 15){
		const mouse = this._toCanvasMouse(e);
		const camera = this.viewer.scene.getActiveCamera();
		const size = this.renderer.getSize(new THREE.Vector2());
		const width = size.width, height = size.height;

		let best = null;
		for(const visual of this.visuals.values()){
			if(!visual.points) continue; // see _findHoveredVertex()'s own guard comment
			const n = visual.points.length;
			if(n < 2) continue;

			const segmentCount = visual.closed ? n : n - 1;
			for(let i = 0; i < segmentCount; i++){
				const a = visual.points[i].position;
				const b = visual.points[(i + 1) % n].position;
				const screenA = projectToScreen(a, camera, width, height);
				const screenB = projectToScreen(b, camera, width, height);
				const t = closestTOnScreenSegment(mouse, screenA, screenB);
				const sx = screenA.x + t * (screenB.x - screenA.x);
				const sy = screenA.y + t * (screenB.y - screenA.y);
				const distPx = Math.hypot(mouse.x - sx, mouse.y - sy);

				if(distPx <= maxPixels && (!best || distPx < best.distPx)){
					const point = new THREE.Vector3(
						a.x + t * (b.x - a.x),
						a.y + t * (b.y - a.y),
						a.z + t * (b.z - a.z),
					);
					best = {visual, segmentIndex: i, t, point, distPx};
				}
			}
		}

		return best;
	}

	// Screen-space hit test against every committed Polygon's fill mesh (see
	// FeatureVisual.enableFill()) - the plain-click counterpart to
	// _findHoveredSegment() for the polygon's INTERIOR: a click near an edge
	// is already caught by _findHoveredSegment()'s own 15px threshold, but a
	// click well inside a large polygon (now a visually prominent, inviting
	// green area) is not - nothing about that area was previously
	// clickable at all, since fillMesh carries no event listeners of its own
	// (unlike a vertex sphere - see FeatureVisual._wireSphere()) and was
	// therefore never part of InputHandler's own interactables list either
	// (see InputHandler.getHoveredElements() - only nodes with at least one
	// registered listener are even raycast-tested). A real THREE.Raycaster
	// against the actual fill geometry is used here rather than a manual
	// screen-space point-in-polygon test, since Mesh raycasting is already
	// the same proven mechanism vertex-sphere hover/click relies on
	// elsewhere in this module - no new hit-testing primitive needed.
	_findHoveredFill(e){
		const mouse = this._toCanvasMouse(e);
		const camera = this.viewer.scene.getActiveCamera();
		const domElement = this.viewer.renderer.domElement;

		const nmouse = new THREE.Vector2(
			(mouse.x / domElement.clientWidth) * 2 - 1,
			-(mouse.y / domElement.clientHeight) * 2 + 1,
		);
		const raycaster = new THREE.Raycaster();
		raycaster.setFromCamera(nmouse, camera);

		for(const visual of this.visuals.values()){
			if(!visual.fillMesh) continue;
			if(raycaster.intersectObject(visual.fillMesh, false).length > 0) return {visual};
		}

		return null;
	}

	// Context-menu revision: the whole-object-only counterpart to
	// _findHoveredFill() above, for registrations with no vertex/segment/
	// fill mechanics of their own to hit-test - in practice, Rør
	// (PipeVisual, a plain THREE.Object3D). Raycasts against the visual
	// cylinder and station marker (its two real Mesh objects) rather than
	// the thin centerline/label/copy-icon, matching how a Pipe's own
	// on-screen footprint is dominated by the cylinder - a click that lands
	// on the pipe's rendered surface should select it, without requiring
	// pixel-precise aim at a thin line. Returns {visual} or null.
	_findHoveredWholeObject(e){
		const mouse = this._toCanvasMouse(e);
		const camera = this.viewer.scene.getActiveCamera();
		const domElement = this.viewer.renderer.domElement;

		const nmouse = new THREE.Vector2(
			(mouse.x / domElement.clientWidth) * 2 - 1,
			-(mouse.y / domElement.clientHeight) * 2 + 1,
		);
		const raycaster = new THREE.Raycaster();
		raycaster.setFromCamera(nmouse, camera);

		for(const visual of this.visuals.values()){
			if(!(visual instanceof PipeVisual)) continue;
			const targets = [visual.cylinderMesh, visual.stationMarker].filter((m) => m && m.visible);
			if(targets.length > 0 && raycaster.intersectObjects(targets, false).length > 0) return {visual};
		}

		return null;
	}

	// ── The one geometry-change lifecycle (stabilisation task §12) ──────────
	//
	// EVERY geometry mutation of a registered feature - a Vektor-operation's
	// applyFeatureGeometry(), a direct sphere drag, a vertex delete - ends up
	// dispatching FeatureVisual's `geometry_changed`. That single event is the
	// authoritative point where the Feature's coordinates AND its snap targets
	// are brought back in sync (wired once per feature in _wireFeatureLifecycle()
	// below). applyFeatureGeometry() therefore does NOT re-register snap
	// targets itself - it would just be a duplicate of what the event handler
	// already did synchronously inside setPoints().
	//
	// applyFeatureGeometry(): commit a recomputed vertex set. Returns false if
	// the feature/visual can't be resolved or the new geometry is invalid
	// (fewer than minVertices points).
	// Trin G1: en integrationsbundet Feature er geometri-låst (se getEditingLocks()), så en BRUGER-initieret geometriændring
	// afvises her (defense in depth ved siden af EditingCapabilities - dette er det fælles commit-punkt for menuernes
	// operationer og Topologi-tjek). `options.force` er kun til host-drevet datasynkronisering (setFeatureGeometryFromData).
	applyFeatureGeometry(visual, newPositions, options = {}){
		const feature = this.getFeature(visual);
		if(!feature) return false;
		if(!options.force && this.getEditingLocks(feature).geometryLocked) return false;

		const applied = visual.setPoints(newPositions); // -> _sync() -> "geometry_changed" -> coords + snap targets
		if(!applied) return false;

		// A geometry change can invalidate a box-selection's captured vertex
		// indices/feature set - clear it unconditionally (simplest safe rule).
		this.clearMultiSelection();

		return true;
	}

	// Wires the parts of the feature lifecycle EVERY registered feature needs,
	// whoever created it: visuals map, FeatureManager, initial snap targets,
	// and the `geometry_changed` -> {updateGeometry + snap-target refresh}
	// sync. Also feature_clicked -> select, and the sphere-drag connection
	// warning hook for vertex-editable features. Shared by registerNewFeature()
	// AND the calculated-geometry tools (crossSection/height/midline/
	// perpendicular/parallel/midpoint/volume), which used to hand-copy a
	// subtly-different version of this and thereby missed the snap refresh on
	// a drag (stabilisation task §11).
	//
	// Callers set visual.setInfo(...) / tool-specific visual state BEFORE
	// calling this.
	//
	// Stempel registreringens model-tilhoerighed HER (Registreringer-
	// tildelings-diagnoseopgaven, 2026-10) - IKKE i registerNewFeature(), som
	// den oprindeligt (forkert) blev antaget at vaere "det eneste faelles
	// sted alle tool-callsites gaar igennem". Ni af vaerktoejerne (crossSection/
	// height/midline/perpendicular/parallelOffset/midpoint/volume/pipeTrace)
	// kalder denne metode DIREKTE (se kommentaren nedenfor om
	// "calculated-geometry tools") og sprang derfor stemplingen helt over -
	// alle deres registreringer endte altid i "Uden mappe", uanset hvilken
	// model der var aktiv. Denne metode er det RIGTIGE faelles sted: alle
	// kendte oprettelsesveje (inkl. registerNewFeature() selv) gaar igennem
	// den, foer featuren foejes til FeatureManager, saa feltet er korrekt fra
	// det allerfoerste 'feature_added'-event. this.activePointcloudId er null,
	// hvis ingen model er aktiv - featuren faar da ogsaa null, samme
	// "Uden mappe"-fallback som for orphan-registreringer generelt.
	_wireFeatureLifecycle(feature, visual){
		// Stempl kun hvis featuren ikke allerede har en model (trin B1): en finaliseret kandidat bærer den capture-kontekst,
		// den blev produceret i (_commitContext), uanset hvilken model der er aktiv, når finaliseringen er færdig. Uden
		// kontekst og uden forudsat pointcloudId er adfærden uændret: den aktive model nu.
		if(feature.pointcloudId == null){
			feature.pointcloudId = this._commitContext ? this._commitContext.pointcloudId : this._pointcloudIdForTool(feature.properties && feature.properties.tool);
		}
		// Registreringer/Målinger (2026-10): stemplet HER, samme sted og samme
		// begrundelse som pointcloudId ovenfor - FolderManager skal kunne
		// afgøre kind-kompatibilitet (aktiv mappe, moveFeature) uden selv at
		// kende til ToolRegistry, og frontend-træet læser denne STEMPLEDE
		// værdi direkte (registreringer-sync.js) i stedet for at udlede den
		// fra tool-id'et ved hver visning - én autoritativ kilde, ikke to der
		// kan afvige. Ukendt/manglende værktøj -> 'measurement' (sikre side).
		// Root cause (fejlopfølgning, 2026-10): tool-id'et ligger på
		// feature.properties.tool (Feature.js), IKKE feature.tool - en
		// ikke-eksisterende top-level property, altid undefined.
		// DigitizationTools.get(undefined) returnerede derfor altid null,
		// og ALT blev stemplet 'measurement', uanset værktøj.
		feature.kind = (DigitizationTools.get(feature.properties.tool) || {}).kind || "measurement";

		this.visuals.set(feature.id, visual);
		this.featureManager.add(feature);
		this.registerFeatureSnapTargets(feature);

		visual.addEventListener("geometry_changed", () => {
			const coords = visual.postProcessCoordinates
				? visual.postProcessCoordinates(visual.rawCoordinates())
				: visual.toCoordinates();

			// Parametric-arc invalidation (task §9) - read BEFORE updateGeometry()
			// mutates feature.geometry.coordinates, so this always compares the
			// PRE-edit coordinates against the fresh ones. Only touches
			// domainData when the feature actually has stored arc ranges
			// (getArcRanges() is empty for the overwhelming majority of
			// features) - see arcRangeStore.js's reconcileArcRanges() for the
			// unconditional invalidation rule itself, and VectorEditingMenu.js's
			// arc-editing dialog for how it re-asserts its OWN range's metadata
			// right after triggering this same listener, so it never
			// self-dissolves the arc it is actively editing.
			const existingArcRanges = getArcRanges(feature);
			if(existingArcRanges.length > 0){
				const reconciled = reconcileArcRanges(feature, feature.geometry.coordinates, coords);
				if(reconciled.length !== existingArcRanges.length){
					this.featureManager.setDomainData(feature.id, "arc", {ranges: reconciled});
				}
			}

			// Parametric whole-feature shape invalidation (Cirkel/Rektangel -
			// task DEL E) - same "read BEFORE updateGeometry() mutates
			// feature.geometry.coordinates" ordering as the arc rule above, and
			// the same "the shape's own editing dialog re-asserts its metadata
			// right after triggering this listener" escape hatch (see
			// geometry/parametricShapeStore.js's own doc comment) - simpler
			// than the arc rule since a Circle/Rectangle always owns its whole
			// feature, never a sub-range, so there is no sibling-reindexing to
			// do, only "does this shape's metadata still match reality". Both
			// namespaces only ever apply to a Polygon feature, whose
			// coordinates are GeoJSON-nested one level deeper than a
			// LineString's own ([[ring]], the outer array holding exactly one
			// ring, plus a repeated closing vertex both old/new share
			// consistently) - unwrapped here so parametricShapeSurvives() can
			// compare plain [x,y,z] arrays exactly like the arc rule does.
			const oldFlat = feature.geometry.type === "Polygon" ? feature.geometry.coordinates[0] : feature.geometry.coordinates;
			const newFlat = feature.geometry.type === "Polygon" ? coords[0] : coords;
			for(const ns of PARAMETRIC_SHAPE_NAMESPACES){
				if(feature.domainData[ns] && !parametricShapeSurvives(oldFlat, newFlat)){
					this.featureManager.clearDomainData(feature.id, ns);
				}
			}

			this.geometryWriteback.beforeChange(feature); // baseline = geometrien FØR denne ændring (kun bundne Features)
			this.featureManager.updateGeometry(feature.id, coords); // mutates `feature` in place
			this.removeFeatureSnapTargets(feature.id);
			this.registerFeatureSnapTargets(feature);
			this.geometryWriteback.afterChange(feature);
		});

		visual.addEventListener("feature_clicked", () => this.selectVisual(visual));

		// duck-typed hook FeatureVisual's sphere-drag handler calls after a
		// direct drag (undefined/no-op for non-vertex visuals)
		visual.onVertexMoved = (index, previousPosition) => this.checkVertexMoveConnectionWarning(visual, index, previousPosition);
	}

	// Aktiv Registreringsprofil for NYE registreringer (trin B1). `profile` er et opakt objekt leveret af hosten:
	//   { canFinalize({candidates, tool, captureContext}) -> boolean,        (valgfri; synkron)
	//     finalize({candidates, tool, captureContext, signal}) -> Promise<resultat | [resultat]> }
	// hvor et resultat er {commit: boolean, patch?: {name?, description?, attributes?, domainData?}, message?}.
	// `null` = PointView standard: ingen finalisering, ingen hooks, ren synkron pass-through. Potree kender ikke
	// profilens art (database, DANVA, ...) - kun dette interface.
	setRegistrationProfile(profile){
		this.registrationProfile = profile || null;
	}

	// Fælles commit-søm (Registreringsprofiler, trin B0/B1): overgangen fra "et Registreringsværktøj har produceret sit
	// resultat" til den eksisterende Feature-lifecycle. `candidates` er [{feature, visual}] - ét eller flere resultater
	// fra samme operation; `options.tool` er værktøjet.
	//
	// PASS-THROUGH (ingen profil, eller profilen kan/vil ikke finalisere dette resultat): hver kandidat registreres præcis
	// som før (registerNewFeature), SYNKRONT og i rækkefølge - ingen Promise, ingen ekstra event-loop-tur. Returnerer
	// kandidaterne uændret. Det er PointView standards eneste vej.
	//
	// FINALISERING (aktiv profil med canFinalize(...) === true): returnerer en Promise<{committed, discarded}>. Se
	// _finalizeCandidates(). Kun kaldere der er routet hertil (standard-capture, F1) kan håndtere en Promise.
	//
	// Routet: standard-capture (buildFeatureIfValid: Punkt/Linje/Polygon). Bevidst IKKE routet: Cirkel/Rektangel/Bue/
	// Farvet areal (registrerer før deres egen dialog), de konstruerede værktøjer der kalder _wireFeatureLifecycle +
	// feature_created direkte (de springer clearMultiSelection() over, som registerNewFeature() kalder - så en omlægning
	// ville ikke være ren ækvivalens), Find rør og Del linje.
	commitCandidates(candidates, options = {}){
		const profile = this.registrationProfile;
		const tool = options.tool || null;

		if(profile && typeof profile.finalize === "function"){
			// capture-konteksten fryses her: den model resultatet blev produceret i
			const captureContext = {pointcloudId: this._pointcloudIdForTool(tool ? tool.id : null)};
			const wants = typeof profile.canFinalize === "function"
				? profile.canFinalize({candidates, tool, captureContext})
				: true;
			if(wants) return this._finalizeCandidates(profile, candidates, tool, captureContext);
		}

		for(const candidate of candidates){
			this.registerNewFeature(candidate.feature, candidate.visual);
		}
		return candidates;
	}

	// Profil-finalisering af en ny kandidat (B1). Kandidaten er provisorisk: dens visual ligger i scenen, men featuren er
	// IKKE i FeatureManager/FolderManager/træ/snap-targets, før profilen har svaret commit:true.
	//
	//  - Profilen og capture-konteksten er snapshottet her; et senere skift af aktiv profil/model påvirker ikke operationen.
	//  - Profilens returnerede slutresultat er AUTORITATIVT. `signal` er en anmodning til profilen om at afbryde (ESC /
	//    værktøjsskift) - IKKE en regel om at kassere: svarer profilen commit:true efter en abort (fx fordi en ekstern
	//    CREATE allerede er startet), committes kandidaten, så der ikke efterlades en forældreløs ekstern post.
	//  - commit:false (eller en fejl) fjerner kandidatens visual; intet andet er oprettet.
	//  - Et commit registreres under den frosne capture-kontekst (_commitContext), så pointcloudId bliver modellen fra
	//    capture-tidspunktet. Metadata-patchen flettes kontrolleret (se _applyCandidatePatch).
	_finalizeCandidates(profile, candidates, tool, captureContext){
		const record = {tool, controller: new AbortController(), aborted: false};
		this._finalizations.add(record);

		let pending;
		try{
			pending = Promise.resolve(profile.finalize({candidates, tool, captureContext, signal: record.controller.signal}));
		}catch(error){
			pending = Promise.reject(error);
		}

		return pending.then((results) => results, (error) => {
			console.error("Registreringsprofil: finalize() fejlede - kandidaten kasseres", error);
			return null;
		}).then((results) => {
			this._finalizations.delete(record);

			const list = Array.isArray(results) ? results : [results];
			const committed = [];
			const discarded = [];
			const messages = [];

			candidates.forEach((candidate, index) => {
				const result = list[index];
				if(result && typeof result.message === "string" && result.message) messages.push(result.message);
				if(result && result.commit === true){
					this._applyCandidatePatch(candidate, result.patch);
					committed.push(candidate);
				}else{
					this.scene.remove(candidate.visual);
					discarded.push(candidate);
				}
			});

			const previousContext = this._commitContext;
			this._commitContext = captureContext;
			try{
				for(const candidate of committed){
					this.registerNewFeature(candidate.feature, candidate.visual);
				}
			}finally{
				this._commitContext = previousContext;
			}

			const outcome = {
				tool,
				committed: committed.map((c) => c.feature),
				discarded: discarded.map((c) => c.feature),
				aborted: record.aborted,
				messages,
			};
			// kun i profil-stien: hosten kan vise profilens besked (fx hvorfor en kandidat blev afvist)
			this.dispatchEvent({type: "registration_finalized", ...outcome});
			return outcome;
		});
	}

	// Kontrolleret metadata-patch på en uregistreret kandidat. Kun disse felter kan patches; geometri, tool, category,
	// featureType og eksisterende værdier røres ikke:
	//   name, description  -> erstatter (hvis angivet som tekst)
	//   attributes         -> tilføjes til properties.attributes; en eksisterende nøgle overskrives IKKE (værktøjets egen data)
	//   domainData         -> et navnerum tilføjes kun hvis det ikke findes (værktøjets egen domænedata røres ikke)
	_applyCandidatePatch(candidate, patch){
		if(!patch || typeof patch !== "object") return;
		const {feature, visual} = candidate;

		if(typeof patch.name === "string" && patch.name) feature.properties.name = patch.name;
		if(typeof patch.description === "string") feature.properties.description = patch.description;

		if(patch.attributes && typeof patch.attributes === "object"){
			if(!feature.properties.attributes) feature.properties.attributes = {};
			for(const [key, value] of Object.entries(patch.attributes)){
				if(!(key in feature.properties.attributes)) feature.properties.attributes[key] = value;
			}
		}

		if(patch.domainData && typeof patch.domainData === "object"){
			for(const [namespace, value] of Object.entries(patch.domainData)){
				if(!(namespace in feature.domainData)) feature.domainData[namespace] = value;
			}
		}

		visual.setInfo(feature.properties.name, feature.properties.description);
	}

	// Anmoder alle igangværende finaliseringer om at afbryde (ESC, værktøjsskift, stop). De markeres aborted med det samme
	// (ingen re-arm på deres vegne); profilen afgør selv, om resultatet stadig committes (AbortSignal er rådgivende).
	_abortFinalizations(){
		if(this._finalizations.size === 0) return;
		const records = Array.from(this._finalizations);
		this._finalizations.clear();
		for(const record of records){
			record.aborted = true;
			record.controller.abort();
		}
	}

	// Er der en igangværende profil-finalisering for dette værktøj? Bruges til ikke at starte en 2. capture med samme
	// værktøj, mens den forrige endnu afventer profilen.
	_isFinalizingTool(tool){
		for(const record of this._finalizations){
			if(record.tool === tool) return true;
		}
		return false;
	}

	// Registers a brand-new Feature+FeatureVisual pair as a first-class,
	// fully-live part of the module (see _wireFeatureLifecycle()) and
	// dispatches 'feature_created'. Used by commitCandidates() (the standard
	// capture path, buildFeatureIfValid()), by the capture-then-dialog tools
	// and by Del linje's second half.
	registerNewFeature(feature, visual){
		// Model-stempling sker nu i _wireFeatureLifecycle() selv (den rigtige
		// faelles chokepoint - se dens egen kommentar).
		this._wireFeatureLifecycle(feature, visual);

		// A new feature appearing (e.g. Del linje's second half) can change
		// what an in-progress box-selection conceptually meant - clear it.
		this.clearMultiSelection();

		this.dispatchEvent({type: "feature_created", feature, visual});

		return visual;
	}

	// En registrering/maaling oprettet af brugeren uden for 3D-capture (fx tegnet i 2D-profilen): samme item-form som
	// addFeaturesFromData, men registreres som brugerens EGEN tegning ('feature_created' - redigerbar, i Registreringer og
	// med i gem/fortryd), ikke som indlaest data. Returnerer featuren, eller null hvis geometrien er ugyldig.
	addUserFeature(item){
		const built = this._buildLoadedFeature(item);
		if(!built) return null;
		this.registerNewFeature(built.feature, built.visual);
		return built.feature;
	}

	// Programmatisk Feature-load (Registreringsprofiler, trin E): en host leverer færdig Geometry + Metadata fra en ekstern
	// kilde, og modulet opretter ALMINDELIGE Features (samme visual, lifecycle, snap, mappe og træ som en tegnet
	// registrering). Potree kender hverken kilden, dens skema, opslagslister eller CRS - koordinaterne er allerede i
	// scenens/modellens koordinatsystem (hosten har transformeret dem).
	//
	//   item = {
	//     geometry:    {type: "Point"|"LineString"|"Polygon", coordinates}   GeoJSON-form, [x, y, z]
	//     pointcloudId: modellen featuren hører til (eksplicit; stemples ikke om af _wireFeatureLifecycle)
	//     id?                                -> featurens id (default: nyt); bruges, når hosten gendanner egne objekter
	//     name?, description?, attributes?   -> properties (klasse A)
	//     domainData?                        -> {<navnerum>: data} (klasse B/C, opakt for modulet)
	//     tool?:   værktøjs-id, der bestemmer kind/ikon/visning (default pr. geometritype, se LOADED_DEFAULT_TOOL)
	//     folder?: {name} -> topniveau-mappe under modellen (kind "registration"), oprettes ved behov; ellers "Uden mappe"
	//   }
	//
	// Ingen 'feature_created' (den hører til brugerens egen tegning) og ingen selection/clearMultiSelection; i stedet ét
	// 'features_loaded'. FolderManagers auto-filing i den aktive mappe neutraliseres under load, så et load aldrig lander
	// i brugerens aktuelle mappe. Ugyldige items springes over. Returnerer {created: [Feature], skipped: antal}.
	addFeaturesFromData(items, options = {}){
		const created = [];
		let skipped = 0;
		const folderManager = this.folderManager;
		const savedActiveFolder = folderManager ? folderManager.activeFolderId : null;
		if(folderManager) folderManager.activeFolderId = null;

		try{
			for(const item of items || []){
				const built = this._buildLoadedFeature(item);
				if(!built){
					skipped++;
					continue;
				}

				this._wireFeatureLifecycle(built.feature, built.visual);

				if(folderManager && item.folder && item.folder.name){
					const kind = built.feature.kind || "registration";
					const folder = this._findOrCreateLoadFolder(item.folder.name, built.feature.pointcloudId, kind);
					if(folder) folderManager.moveFeature(built.feature.id, folder.id);
				}

				created.push(built.feature);
			}
		}finally{
			if(folderManager) folderManager.activeFolderId = savedActiveFolder;
		}

		if(created.length > 0){
			this.dispatchEvent({type: "features_loaded", features: created});
		}

		return {created, skipped};
	}

	// Erstatter en eksisterende featurs geometri med færdige koordinater (samme GeoJSON-form som addFeaturesFromData).
	// Går gennem den almindelige geometri-livscyklus (applyFeatureGeometry -> geometry_changed). false = ukendt feature/ugyldig.
	setFeatureGeometryFromData(featureId, geometry){
		const visual = this.visuals.get(featureId);
		const positions = visual ? this._positionsFromGeometry(geometry) : null;
		if(!visual || !positions) return false;
		// Host-drevet (load/rollback): kilden er sandheden, så ændringen er aldrig en bruger-ændring der skal skrives tilbage.
		const applied = this.geometryWriteback.runSuppressed(() => this.applyFeatureGeometry(visual, positions, {force: true}));
		if(applied) this.geometryWriteback.reset(featureId);
		return applied;
	}

	// GeoJSON-geometri -> [THREE.Vector3] (punkter/vertices; Polygon uden lukke-punktet). null hvis ugyldig.
	_positionsFromGeometry(geometry){
		if(!geometry || !geometry.type || !Array.isArray(geometry.coordinates)) return null;

		let coords;
		if(geometry.type === "Point") coords = [geometry.coordinates];
		else if(geometry.type === "LineString") coords = geometry.coordinates;
		else if(geometry.type === "Polygon" && Array.isArray(geometry.coordinates[0])){
			coords = geometry.coordinates[0].slice();
			const first = coords[0], last = coords[coords.length - 1];
			if(coords.length > 1 && first && last && first[0] === last[0] && first[1] === last[1]) coords.pop();
		}else return null;

		const positions = [];
		for(const c of coords){
			if(!Array.isArray(c) || !isFinite(c[0]) || !isFinite(c[1])) return null;
			positions.push(new THREE.Vector3(c[0], c[1], isFinite(c[2]) ? c[2] : 0));
		}

		return positions.length > 0 ? positions : null;
	}

	// Feature + visual fra et load-item (uden at registrere). null hvis item/geometri er ugyldig.
	_buildLoadedFeature(item){
		if(!item || !item.geometry) return null;
		const positions = this._positionsFromGeometry(item.geometry);
		if(!positions) return null;

		const geometryType = item.geometry.type;
		const toolId = item.tool || LOADED_DEFAULT_TOOL[geometryType];
		const tool = DigitizationTools.get(toolId) || {};

		// A tool whose finished visual is not a FeatureVisual (e.g. the pipe: a computed cylinder, no draggable vertices) supplies it
		// through tool.createLoadedVisual(item), built from the canonical feature data only; null falls back to the plain visual.
		let customVisual = null;
		if(typeof tool.createLoadedVisual === "function"){
			try{
				customVisual = tool.createLoadedVisual(item) || null;
			}catch(error){
				console.error(`createLoadedVisual failed for ${toolId}`, error);
			}
		}
		let visual = customVisual;
		if(customVisual){
			this.scene.add(customVisual);
		}else{
			visual = new FeatureVisual(geometryType);
			if(positions.length < visual.minVertices) return null;

			visual.postProcessCoordinates = tool.postProcessCoordinates || null;
			if(tool.suppressVertexLabels) visual.setVertexLabelsSuppressed(true);
			if(tool.segmentLabelFormat) visual.enableSegmentLabels(tool.segmentLabelFormat);
			if(tool.summaryLabelFormat) visual.enableSummaryLabel(tool.summaryLabelFormat, tool.summaryLabelAnchor);
			if(tool.fillColor) visual.enableFill(tool.fillColor, tool.fillOpacity);
			this.scene.add(visual);
			visual.setPoints(positions);
		}

		const feature = createFeature({
			geometryType,
			coordinates: customVisual ? item.geometry.coordinates : visual.toCoordinates(),
			category: tool.category,
			tool: toolId,
			featureType: tool.featureType || tool.name,
			name: item.name || `${tool.name || geometryType} ${this.featureManager.features.length + 1}`,
			description: item.description || "",
			attributes: item.attributes || {},
			domainData: item.domainData || {},
			pointcloudId: item.pointcloudId != null ? item.pointcloudId : null,
		});
		// En vært, der gendanner sine egne objekter (fx POI'er fra lokal lagring), beholder objektets id
		if(item.id != null && item.id !== "") feature.id = String(item.id);
		visual.setInfo(feature.properties.name, feature.properties.description);

		// Restore extension point: a tool whose finished result has presentation that its capture code sets (result/segment labels,
		// measurement helper shapes that stay on the finished object, per-vertex role names, fill override) re-applies it here from
		// the canonical feature data, with the same helpers the capture flow uses. It must NOT recreate capture-only state (source
		// selections, temporary construction geometry). `decorateLoaded(visual, feature, dm)`; a failing hook never drops the feature.
		if(!customVisual && typeof tool.decorateLoaded === "function"){
			try{
				tool.decorateLoaded(visual, feature, this);
			}catch(error){
				console.error(`decorateLoaded failed for ${toolId}`, error);
			}
		}

		return {feature, visual};
	}

	// Topniveau-mappe (navn + model + kind) - findes eller oprettes. Returnerer mappen.
	_findOrCreateLoadFolder(name, pointcloudId, kind){
		const fm = this.folderManager;
		const existing = fm.folders.find((f) => f.parentId === null && f.name === name && f.pointcloudId === (pointcloudId != null ? pointcloudId : null) && f.kind === kind);
		return existing || fm.createFolder(name, null, null, pointcloudId, kind);
	}

	// Coincident-endpoint detection - INFORMATION ONLY. Basic edit operations
	// (Flyt / Snap / sphere drag) never branch on this; geometric coincidence
	// is not shared editing identity (stabilisation task §1.1 / §1.2 / §3). It
	// is consumed by: (a) the Topology-QA panel, (b) checkVertexMoveConnectionWarning()
	// below - the non-blocking "you moved one end of a junction" toast.
	//
	// Pure query, no side effects: returns the coincident-endpoint group (see
	// geometry/topologyQa.js findCoincidentEndpointGroups()) this endpoint
	// currently belongs to, or null if it isn't an endpoint / shares no other
	// feature's endpoint. `features` defaults to the current list;
	// checkVertexMoveConnectionWarning() passes an already-patched snapshot
	// for its after-the-fact check.
	findConnectionGroup(visual, vertexIndex, features = this.featureManager.features){
		const feature = this.getFeature(visual);
		if(!feature) return null;

		const type = feature.geometry.type;
		const isEndpoint = type === "Point" || (type === "LineString" && (vertexIndex === 0 || vertexIndex === visual.points.length - 1));
		if(!isEndpoint) return null;

		const tolerance = settings.get("topology.planarTolerance");
		const group = findCoincidentEndpointGroups(features, {tolerance}).find((g) =>
			g.members.some((m) => m.featureId === feature.id && m.endpointIndex === vertexIndex));
		if(!group || group.members.length < 2) return null;

		return group;
	}

	// `previousPosition` (the position BEFORE this move, {x,y,z} or a
	// THREE.Vector3) is required because by the time this runs, `visual`'s
	// own FeatureVisual has already _sync()'d and, via the geometry_changed
	// listener wired in registerNewFeature() above, FeatureManager already
	// reflects the NEW position - the coincidence check is done against a
	// features snapshot with just THIS vertex patched back to its old spot,
	// so every OTHER feature's own (unaffected, current) endpoints are still
	// compared correctly.
	//
	// After an endpoint has moved (sphere drag, Flyt punkt, Snap til punktsky,
	// Snap til andet punkt) and its OLD position was part of a coincident-
	// endpoint cluster with another feature, shows a brief non-blocking toast
	// ("Forbindelsen til X blev brudt"). Information only - never blocks or
	// undoes the move, and the move already acted on the selected vertex alone
	// (stabilisation task §3). No persistent node model - recomputed on demand.
	checkVertexMoveConnectionWarning(visual, vertexIndex, previousPosition){
		const feature = this.getFeature(visual);
		if(!feature) return;

		const type = feature.geometry.type;
		const prevCoord = [previousPosition.x, previousPosition.y, previousPosition.z];
		const patchedFeatures = this.featureManager.features.map((f) => {
			if(f.id !== feature.id) return f;
			if(type === "Point") return {...f, geometry: {...f.geometry, coordinates: prevCoord}};
			const coords = f.geometry.coordinates.slice();
			coords[vertexIndex] = prevCoord;
			return {...f, geometry: {...f.geometry, coordinates: coords}};
		});

		const group = this.findConnectionGroup(visual, vertexIndex, patchedFeatures);
		if(!group) return;

		const others = group.members.filter((m) => m.featureId !== feature.id);
		const names = others.map((m) => m.featureName).join(", ");
		showToast(t("Forbindelsen til {0} blev brudt (kun {1} flyttet)", names, feature.properties.name), 4500);
	}

	// Integrationsbinding (Registreringsprofiler, trin F). Hosten registrerer opake adaptere pr. `domainData`-navnerum
	// (fx {db: adapter}); modulet kender hverken kilden, skemaet eller HTTP. En Feature er integrationsstyret, når
	// `feature.domainData[navnerum]` findes for et registreret navnerum - bindingen på featuren er autoritativ, den aktive
	// profil spiller ingen rolle. Adapter-kontrakt (alle valgfri):
	//   getFeatureActions(feature, binding)           -> [{id, title, icon?}]    handlinger i registreringsmenuen
	//   runFeatureAction(actionId, feature, binding)  -> Promise<any>
	//   beforeRemove({feature, binding})              -> Promise<{proceed, message?}>   bruger-initieret sletning
	//   getEditingConstraints(feature, binding)       -> {geometryLocked?: boolean}     false åbner geometri-redigering
	//   persistGeometry({feature, binding, geometry, previousGeometry}) -> Promise<{ok, message?}>   (trin H)
	//     kaldes ÉN gang pr. afsluttet bruger-ændring af geometrien; {ok:false} (eller en fejl) ruller lokalt tilbage.
	setIntegrationAdapters(adapters){
		this.integrationAdapters = adapters || {};
		this.refreshEditingLocks();
	}

	// Genberegner geometri-låsen på alle Features (host kalder den, når adapterens tilstand ændres, fx destination bliver
	// skrivbar/ikke-skrivbar efter model- eller kontekstskift).
	refreshEditingLocks(){
		for(const feature of this.featureManager.features) this._syncEditLock(feature);
	}

	// Redigerings-laase for en Feature ud fra dens integrationsbinding (trin G1): {geometryLocked, structureLocked}.
	//   - ubundet Feature: ingen laase (uændret redigering).
	//   - bundet: structureLocked er altid sand (permanent: split/merge/trim m.fl. ændrer række-/objektsemantik);
	//     geometryLocked er sand, medmindre adapterens getEditingConstraints() eksplicit siger geometryLocked === false
	//     (først når geometri-writeback findes). Mangler/fejler adapteren, er begge sande (bindingen er autoritativ).
	getEditingLocks(feature){
		const hit = this._resolveIntegration(feature);
		if(!hit) return {geometryLocked: false, structureLocked: false};

		let constraints = null;
		if(typeof hit.adapter.getEditingConstraints === "function"){
			try{
				constraints = hit.adapter.getEditingConstraints(feature, hit.binding);
			}catch(error){
				console.error("Integrations-adapter: getEditingConstraints fejlede", error);
			}
		}

		return {geometryLocked: !constraints || constraints.geometryLocked !== false, structureLocked: true};
	}

	// Lægger geometri-låsen på featurens visual (`editable`), så direkte træk, indsæt-håndtag og dobbeltklik-sletning
	// følger bindingen. Rører kun visualer, den selv har låst (andre værktøjer sætter ikke `editable`).
	_syncEditLock(feature){
		const visual = feature && this.visuals.get(feature.id);
		if(!visual) return;

		const locked = this.getEditingLocks(feature).geometryLocked;
		if(locked){
			visual.editable = false;
			visual.userData.integrationLocked = true;
		}else if(visual.userData.integrationLocked){
			visual.editable = true;
			visual.userData.integrationLocked = false;
		}
	}

	// {namespace, binding, adapter} for en integrationsstyret Feature, ellers null.
	_resolveIntegration(feature){
		if(!feature || !feature.domainData) return null;
		for(const [namespace, adapter] of Object.entries(this.integrationAdapters)){
			const binding = feature.domainData[namespace];
			if(adapter && binding) return {namespace, binding, adapter};
		}
		return null;
	}

	// Er featuren integrationsstyret (binding i et registreret navnerum)? Til visning (fx "bundet"-mærke i træet).
	isIntegrationBound(featureId){
		return !!this._resolveIntegration(this.featureManager.get(featureId));
	}

	// Integrationens handlinger for en Feature (tom liste for ubundne Features eller adaptere uden handlinger).
	getFeatureActions(featureId){
		const feature = this.featureManager.get(featureId);
		const hit = this._resolveIntegration(feature);
		if(!hit || typeof hit.adapter.getFeatureActions !== "function") return [];
		try{
			return hit.adapter.getFeatureActions(feature, hit.binding) || [];
		}catch(error){
			console.error("Integrations-adapter: getFeatureActions fejlede", error);
			return [];
		}
	}

	runFeatureAction(featureId, actionId){
		const feature = this.featureManager.get(featureId);
		const hit = this._resolveIntegration(feature);
		if(!hit || typeof hit.adapter.runFeatureAction !== "function") return Promise.resolve(undefined);
		return Promise.resolve()
			.then(() => hit.adapter.runFeatureAction(actionId, feature, hit.binding))
			.catch((error) => {
				console.error("Integrations-adapter: runFeatureAction fejlede", error);
				showToast(t("Handlingen kunne ikke udføres."), 4500);
			});
	}

	// BRUGER-INITIERET sletning af en Feature ("slet dette objekt": trærække, kontekstmenu, mappe-slet, vertex-slet der
	// fjerner hele objektet). En integrationsstyret Feature går først gennem sin adapters beforeRemove (ekstern DELETE) og
	// fjernes lokalt KUN ved {proceed: true}; ved afvisning/fejl bliver den stående, og beskeden vises. Ubundne Features
	// fjernes straks og synkront som før. Interne fjernelser (merge/split, load-sync, model fjernet) bruger fortsat
	// removeFeature() og rører aldrig en adapter. Returnerer Promise<{removed, message?}>.
	requestRemoveFeature(featureId, options = {}){
		const feature = this.featureManager.get(featureId);
		if(!feature) return Promise.resolve({removed: false});

		const hit = this._resolveIntegration(feature);
		if(!hit || typeof hit.adapter.beforeRemove !== "function"){
			this.removeFeature(featureId);
			return Promise.resolve({removed: true});
		}

		if(this._pendingRemovals.has(featureId)) return Promise.resolve({removed: false, pending: true});
		this._pendingRemovals.add(featureId);

		return Promise.resolve()
			.then(() => hit.adapter.beforeRemove({feature, binding: hit.binding}))
			.catch((error) => {
				console.error("Integrations-adapter: beforeRemove fejlede", error);
				return {proceed: false, message: t("Objektet kunne ikke slettes. Det er bevaret.")};
			})
			.then((result) => {
				this._pendingRemovals.delete(featureId);
				if(result && result.proceed === true){
					if(this.featureManager.get(featureId)) this.removeFeature(featureId);
					return {removed: true};
				}
				const message = (result && result.message) || t("Objektet kunne ikke slettes. Det er bevaret.");
				if(!options.silent) showToast(message, 5000);
				return {removed: false, message};
			});
	}

	removeFeature(featureId){
		this.geometryWriteback.reset(featureId);
		const visual = this.visuals.get(featureId);
		if(visual){
			// Selection/highlight audit finding: this never used to check
			// against selectedVisual at all - removing the currently-selected
			// feature (e.g. Saml med anden linje/Merge removes the picked
			// REFERENCE feature, which may be whatever the user had selected
			// before opening the merge menu on a different feature) left
			// selectedVisual pointing at a disposed, scene-removed object.
			if(this.selectedVisual === visual) this.selectVisual(null);

			this.scene.remove(visual);
			// duck-typed: FeatureVisual/PipeVisual both expose dispose() to
			// free the GPU geometry/material/texture resources scene-graph
			// removal alone does not release (see FeatureVisual.dispose()'s
			// own comment) - a plain Object3D with no such method is left as-is
			if(visual.dispose) visual.dispose();
			this.visuals.delete(featureId);
		}
		this.removeFeatureSnapTargets(featureId);
		this.featureManager.remove(featureId);

		// Same reasoning as applyFeatureGeometry()/registerNewFeature(): a
		// removed feature can invalidate an in-progress box-selection
		// (whether or not it was selectedVisual - it may simply have been
		// one of several multi-selected entries), so this always clears it.
		this.clearMultiSelection();
	}

	selectFeature(featureId){
		this.selectVisual(this.visuals.get(featureId) || null);
	}

	// deactivates the current tool without starting a new capture - the
	// counterpart to startInsertion() staying active indefinitely. Keeps
	// whatever partial capture is already valid (e.g. a Line with 3+ points),
	// same as switching to a different tool mid-capture always has - for a
	// full discard-everything cancel, see cancelActiveTool().
	stopInsertion(){
		this.activeTool = null;
		this._operationActive = false;
		this._operationPointcloudId = undefined;
		this._abortFinalizations();
		this.viewer.dispatchEvent({type: 'cancel_insertions'});
		this._hideToolHint();
		this.dispatchEvent({type: 'active_tool_changed', tool: null});
	}

	// ESC: hard-cancels whatever the active tool is currently capturing and
	// deactivates it. Unlike stopInsertion()/switching tools - which keep an
	// already-valid partial capture (e.g. a Line with 3+ points) - this
	// discards it unconditionally: no feature is ever committed from an ESC.
	// Every tool's own 'cancel_insertions' handler must check e.discard and
	// skip its normal "save what's already valid" step when it's set.
	cancelActiveTool(){
		if(!this.activeTool) return;

		this.activeTool = null;
		this._operationActive = false;
		this._operationPointcloudId = undefined;
		this._abortFinalizations();
		this.viewer.dispatchEvent({type: 'cancel_insertions', discard: true});
		this._hideToolHint();
		this.dispatchEvent({type: 'active_tool_changed', tool: null});
	}

	// ESC's (og det almindelige højreklik-i-idles) fælles udgang fra et værktøj: afbryd evt. interaktive redigeringsforløb,
	// ryd flervalg, forlad værktøjet (hård cancel, ingen delvis registrering) og fravælg en enkelt markeret feature.
	// Panel-lukning (ESC niveau 3) hører IKKE med - den er ESC-specifik og kun relevant uden aktivt værktøj.
	_exitActiveToolLikeEscape(){
		// Abort any pending interactive editing flow (reference-line
		// picking for Trim/Forlæng/Saml/Sæt punkt ved skæring, Flyt punkt,
		// Vælg sammenhængende del, Omtegn del, ...). These listen on the
		// viewer for 'cancel_insertions' but are NOT tied to
		// `this.activeTool`, so cancelActiveTool() alone would miss them
		// whenever no capture tool happens to be armed (each flow's own
		// instruction bar literally promises "ESC annullerer"). The core
		// Potree tools' own cancel_insertions handlers are self-removing
		// and guarded, so an extra broadcast here is safe/idempotent.
		this.viewer.dispatchEvent({type: 'cancel_insertions', discard: true});

		if(this.multiSelection) this.clearMultiSelection();

		this.cancelActiveTool();

		// Clear a plain single-feature selection too - previously the ONE
		// piece of selection state ESC never reset, so a feature clicked
		// once stayed selected (keeping its orange highlight) with no way
		// to deselect it short of clicking another feature. A plain
		// selection highlight is passive UI state, not an in-progress
		// operation - deselecting it is lightweight enough to still allow
		// falling through to level 3 below in the SAME keystroke (task §7
		// only asks to avoid combining an ACTIVE TOOL cancellation with a
		// panel close, not a mere deselect).
		this.selectVisual(null);
	}

	// Er værktøjets workflow i gang (første klik/valg sket)? Se _operationActive ovenfor. Et værktøj, hvis operation-state
	// ligger uden for capture-primitiverne (fx PrecisionMeasure-engine'ens valgte flade), kan levere det via en valgfri
	// `tool.isOperationActive(dm)`.
	isOperationActive(){
		if(this._operationActive) return true;
		if(this._hasOpenToolDialog()) return true;
		const tool = this.activeTool;
		return !!(tool && typeof tool.isOperationActive === "function" && tool.isOperationActive(this));
	}

	// Kaldes af capture-primitiverne ved første brugerhandling (første placerede punkt/valg). Idempotent.
	_markOperationStarted(){
		this._operationActive = true;
	}

	// Dialog-sporing (se _openToolDialogs ovenfor). createDialogOverlay() melder sin overlay (lukkes ved at blive fjernet fra
	// DOM'en); HostDialog.requestHostDialog() melder åbning/lukning af en værts-dialog.
	_trackToolDialog(overlay){
		this._openToolDialogs.add(overlay);
	}

	_hostDialogOpened(){
		this._openHostDialogs++;
	}

	_hostDialogClosed(){
		this._openHostDialogs = Math.max(0, this._openHostDialogs - 1);
	}

	_hasOpenToolDialog(){
		for(const overlay of this._openToolDialogs){
			if(!overlay.isConnected) this._openToolDialogs.delete(overlay);
		}
		return this._openToolDialogs.size > 0 || this._openHostDialogs > 0;
	}

	// Convenience wrapper for external hosts (e.g. a consuming app's own UI,
	// which only knows a tool by its registry id, not the full tool
	// definition object) - looks the tool up in DigitizationTools and starts
	// it the same way a sidebar button click does. No-ops (does nothing) on
	// an unknown id rather than throwing, since a host UI button firing this
	// should never be able to crash the viewer.
	startInsertionById(toolId){
		const tool = DigitizationTools.get(toolId);
		if(!tool) return;

		this.startInsertion(tool);
	}

	// Same "host only knows the registry id" reasoning as startInsertionById()
	// above - every registered tool (see ToolRegistry.js/tools/*.js) carries
	// its own longer, descriptive `tooltip` field, distinct from the short
	// name a host UI shows on the button itself. A host UI's own tool list is
	// typically hand-written (so it can run each label through its own
	// i18n system) rather than read directly off the registry, so without
	// this lookup the richer tooltip text has no way to reach that UI at
	// all - returns null (never throws) for an unknown id, same contract as
	// startInsertionById()'s no-op.
	getToolTooltip(toolId){
		const tool = DigitizationTools.get(toolId);
		return (tool && tool.tooltip) || null;
	}

	// Værktøjslinjens faglige grupper (Geonote-frontendens topbar: Registrering/
	// Måling/Analysevisninger/Rapporter) som ren data - læst direkte fra
	// registeret, så navn/ikon/tooltip/rækkefølge ejes ét sted (værktøjets egen
	// definition) og frontenden ikke skal vedligeholde en parallel liste.
	// Gruppen er værktøjets `toolbarGroup` (bevidst IKKE `kind`: kind styrer
	// Registreringer/Målinger-træet og eksport, mens en tværprofilrapport eller
	// en analysevisning ikke passer ind i registration/measurement), sorteret
	// efter `toolbarOrder`. Værktøjer uden toolbarGroup vises ikke.
	// -> {registration: [...], measurement: [...], analysis: [...], report: [...]}
	// hvor hvert element er {id, name, icon, tooltip, kind}. Selve gruppenavne/
	// ikoner ejes af host-UI'en (i18n).
	getToolbarModel(){
		const groups = {registration: [], measurement: [], analysis: [], report: []};
		const tools = DigitizationTools.list()
			.filter((t) => t.toolbarGroup && groups[t.toolbarGroup])
			.sort((a, b) => (a.toolbarOrder ?? 0) - (b.toolbarOrder ?? 0));
		for(const t of tools){
			groups[t.toolbarGroup].push({id: t.id, name: t.name, icon: t.icon || null, tooltip: t.tooltip || null, kind: t.kind || "measurement"});
		}
		return groups;
	}

	// Same "single stable entry point for an external host UI" reasoning as
	// startInsertionById() above - runs the topology scan and shows its
	// result overlay (both already fully self-contained, no host-supplied
	// container needed - see runTopologyQaScan()'s own doc comment).
	runTopologyScan(){
		runTopologyQaScan(this);
	}

	// Topologi-tjek som DATA til en vært-UI (PointViews ikke-modale Panel): selve scanningen, "Zoom til" og "Luk gap" ejes
	// fortsat af Potree (TopologyQaPanel.js); værten ejer kun visningen af resultatlisten. closeTopologyGap() re-validerer
	// gappet og returnerer false, hvis det ikke længere findes (panelet er ikke-modalt, så listen kan være forældet).
	scanTopology(){ return scanTopology(this); }
	zoomToTopologyFinding(kind, finding){ return zoomToTopologyFinding(this, kind, finding); }
	closeTopologyGap(gap, direction){ return closeTopologyGap(this, gap, direction); }

	// Mounts the Hjælp panel into `container` (a raw DOM element - a host UI
	// only needs to hand this a plain <div>, no jQuery/sidebar coupling, see
	// mountDigitizationHelpPanel()'s own doc comment on accepted container
	// shapes). Returns the same handle mountDigitizationHelpPanel() does.
	// Hjælp-indhold som ren data til en host-UI, der selv tegner hjælpen
	// (Geonote-frontendens Vue-modal). Samme model som mountHelpPanel bruger,
	// så indholdet kun vedligeholdes ét sted (help/helpModel.js).
	// Registreringer-træet (Geonote-frontendens Vue-panel) som ren data: de
	// registrerede kategorier og værktøjer ER allerede taxonomien - frontenden
	// skal ikke gengive den (navne, rækkefølge, ikoner) i en parallel liste.
	// categories: [{id, name, order}] sorteret; getTool(id): {id, name,
	// category, icon, kind} | null. Data uden reference til registrenes egne
	// objekter.
	// `kind` ('registration' | 'measurement', Registreringer/Målinger-
	// opdelingen, 2026-10): ÉN fælles klassifikation ejet af selve
	// værktøjsdefinitionen (se hver tools/*.js-fils eget `kind`-felt) -
	// frontendens træ og Gem-flow læser begge DENNE samme kilde, så de aldrig
	// kan afvige. Ikke det samme som `category` (DigitizationCategories er en
	// ren toolbar-gruppering, fx "Specialiseret registrering" indeholder
	// begge slags).
	getRegistreringerModel(){
		return {
			categories: DigitizationCategories.list().map((c) => ({id: c.id, name: c.name, order: c.order ?? 0})),
			getTool: (id) => {
				const t = DigitizationTools.get(id);
				return t ? {id: t.id, name: t.name, category: t.category, icon: t.icon, kind: t.kind || "measurement"} : null;
			},
		};
	}

	// Omdøber en registrering via den samme autoritative vej som Egenskaber-
	// panelet (FeaturePanel): FeatureManager (fyrer feature_changed) + visualens
	// eget navn/info.
	renameFeature(featureId, name){
		// integrationsbundet (trin G1): visningsnavnet udledes af databasen og ville divergere lokalt - afvises
		if(this._resolveIntegration(this.featureManager.get(featureId))) return false;
		const feature = this.featureManager.get(featureId);
		const clean = name && String(name).trim();
		if(!feature || !clean || feature.properties.name === clean) return false;
		this.featureManager.updateProperties(featureId, {name: clean});
		const visual = this.visuals.get(featureId);
		if(visual && typeof visual.setInfo === "function") visual.setInfo(clean, visual.description);
		return true;
	}

	// Sat fra Vue-siden, hver gang brugeren skifter "aktiv model" i
	// toolbarens modelkontrol (og én gang ved foerste indlaesning) - laeses
	// udelukkende af registerNewFeature() for at stemple NYE registreringer.
	// Aendrer aldrig eksisterende registreringers pointcloudId; at skifte
	// aktiv model paavirker derfor kun registreringer, der oprettes HEREFTER.
	setActivePointcloud(pointcloudId){
		const next = pointcloudId != null ? pointcloudId : null;
		// Skiftes modellen MIDT i en operation (flerklik-værktøj, beregnet værktøj, åben værktøjsdialog), fryses den model, operationen
		// blev påbegyndt under (den forrige) - kun når der var en rigtig model at beskytte (null = intet at fastfryse, fx under
		// automatisk indlæsning af punktskyen ved værktøjsstart). Uden skift midt i en operation sker der intet her.
		if(next !== this.activePointcloudId && this.activePointcloudId != null && this.activeTool && this._operationPointcloudId === undefined && this.isOperationActive()){
			this._operationPointcloudId = this.activePointcloudId;
		}
		this.activePointcloudId = next;
	}

	// Hvilke modeller der er indlæst i 3D lige nu (værten kalder den, hver gang listen ændres). En registrering hører til en model
	// (feature.pointcloudId) og skal kun ses, mens modellen er indlæst: de andre skjules i 3D (og Registreringer-træet filtrerer dem på
	// samme liste). Brugerens eget valg af øje huskes og lægges tilbage, når modellen igen er indlæst. Registreringer uden model
	// (pointcloudId null) og lag (GIS, IFC m.fl.) berøres ikke. ids = null: ingen afgrænsning.
	setLoadedPointclouds(ids){
		this._loadedPointcloudIds = Array.isArray(ids) ? new Set(ids) : null;
		const loaded = this._loadedPointcloudIds;
		let changed = false;
		for(const [featureId, visual] of this.visuals){
			const feature = this.featureManager.get(featureId);
			if(!feature) continue;
			const owner = feature.pointcloudId != null ? feature.pointcloudId : null;
			const hide = !!loaded && owner !== null && !loaded.has(owner);
			if(hide && !visual.userData.modelHidden){
				visual.userData.eyeBeforeModelHide = visual.visible !== false;
				visual.userData.modelHidden = true;
				visual.visible = false;
				changed = true;
			}else if(!hide && visual.userData.modelHidden){
				visual.userData.modelHidden = false;
				visual.visible = visual.userData.eyeBeforeModelHide !== false;
				changed = true;
			}
		}
		if(changed) this.dispatchEvent({type: "model_visibility_changed"});
	}

	// Er en registrering lige nu skjult, fordi dens model ikke er indlæst?
	isHiddenByModel(featureId){
		const visual = this.visuals.get(featureId);
		return !!visual && !!visual.userData.modelHidden;
	}

	// Modellen en ny registrering fra værktøjet `toolId` skal stemples med: den fastfrosne (modellen operationen blev påbegyndt under),
	// hvis modellen er skiftet midt i den pågældende operation - ellers den levende aktive model. Registreringer, der ikke kommer fra
	// det aktive værktøj (2D-profil, indlæste data, native objekter), bruger altid den levende model.
	_pointcloudIdForTool(toolId){
		if(this._operationPointcloudId !== undefined && this.activeTool && toolId != null && toolId === this.activeTool.id){
			return this._operationPointcloudId;
		}
		return this.activePointcloudId;
	}

	// "Flyt til model...": aendrer en registrerings faglige model-ejerskab
	// eksplicit (IKKE almindelig mappe-organisering, som aldrig roerer dette
	// felt - se FolderManager.moveFeature()'s egen invariant-vagt). Fjerner
	// samtidig registreringen fra sin nuvaerende mappe (-> "Uden mappe"), saa
	// den lander direkte i den nye models rod - den gamle mappeplacering
	// foelger bevidst ikke med (se opgavens eksempel: "Model A └─ Kontrol"
	// bliver til en tom mappe, "Linje 1" lander direkte under Model B).
	moveFeatureToPointcloud(featureId, pointcloudId){
		// integrationsbundet (trin G1): modeltilknytningen er en del af bindingen - kan ikke flyttes lokalt
		if(this._resolveIntegration(this.featureManager.get(featureId))) return false;
		const feature = this.featureManager.get(featureId);
		if(!feature) return false;
		const target = pointcloudId != null ? pointcloudId : null;
		if(feature.pointcloudId === target) return false;
		this.featureManager.setPointcloud(featureId, target);
		if(this.folderManager) this.folderManager.moveFeature(featureId, null);
		return true;
	}

	// "Slet mappe og indhold": alle registreringer i mappen og dens undermapper
	// slettes gennem den autoritative livscyklus (removeFeature - visuals,
	// snap-targets, selection, feature_removed), og derefter fjernes selve
	// mappestrukturen. FolderManager rører aldrig features/geometri.
	//
	// Trin F: integrationsstyrede Features (domainData-binding med adapter) slettes hver for sig gennem
	// requestRemoveFeature(); en fejl lader netop dén Feature stå. Mappestrukturen fjernes kun, hvis ALLE registreringer
	// blev slettet (ellers bevares mappen med de resterende, så intet "halvt slettes"). Uden bundne Features er adfærden
	// uændret og synkron. Returnerer false (ukendt mappe), true (alt slettet) eller Promise<{removed, kept}> (bundne).
	deleteFolderAndContents(folderId){
		const fm = this.folderManager;
		if(!fm || !fm.get(folderId)) return false;

		const ids = fm.getFeatureIdsIn(folderId);
		const bound = ids.filter((id) => this._resolveIntegration(this.featureManager.get(id)));
		if(bound.length === 0){
			for(const id of ids) this.removeFeature(id);
			fm.deleteFolderTree(folderId);
			return true;
		}

		return (async () => {
			let removed = 0;
			let kept = 0;
			let firstMessage = null;
			for(const id of ids){
				const result = await this.requestRemoveFeature(id, {silent: true});
				if(result.removed) removed++;
				else{
					kept++;
					if(!firstMessage && result.message) firstMessage = result.message;
				}
			}
			if(kept === 0) fm.deleteFolderTree(folderId);
			else {
				const tail = firstMessage ? ": " + firstMessage : ".";
				showToast(kept === 1 ? t("{0} objekt blev ikke slettet{1}", kept, tail) : t("{0} objekter blev ikke slettet{1}", kept, tail), 6000);
			}
			return {removed, kept};
		})();
	}

	// --- Gem registreringer (eksport) - tynd facade mod den eksisterende
	// eksportmotor (ExportRegistry + exporters), se export/exportFacade.js.
	// Host-UI'et (Geonote-frontendens Vue-dialog) kender hverken registret,
	// CsvProfileStore eller de konkrete exporters.

	// Formater som ren data: [{id, name, extension, description, crsHandling, options}].
	getExportModel(){
		return buildExportModel(ExportRegistry);
	}

	// Filnavnet en eksport ville få (samme konvention som selve eksporten).
	getExportFilename(formatId, {pointcloudName = null, label = null, exportDate = new Date()} = {}){
		const exporter = ExportRegistry.get(formatId);
		return exporter ? previewExportFilename(exporter, {pointcloudName, label, exportDate}) : "";
	}

	// Eksporterer de angivne registreringer (feature-id'er, i den rækkefølge de
	// er givet) med det valgte format. Resultat: {filename, text | blob,
	// mimeType, count}. Kaster ved fejl - intet ændres i FeatureManager/
	// FolderManager. pointcloudName/epsg/projection: host-appens autoritative
	// værdier; mangler pointcloudName, bruges viewerens (GUID) som fallback.
	// projection (Registreringer-model-CRS-opgaven, 2026-10): host-appen kan nu
	// sende en konkret WKT-definition for DENNE eksports featureIds (fx slået
	// op pr. model/EPSG hos kalderen) - mangler den, falder metoden tilbage til
	// den hidtidige adfærd (viewer.getProjection(), den FØRSTE indlæste
	// punktskys egen metadata). Kalderen er ansvarlig for at opdele featureIds
	// i grupper med ét CRS hver og kalde denne metode én gang pr. gruppe - der
	// er ingen gruppering/opdeling her, samme enkle "ét kald, ét resultat"-
	// kontrakt som hidtil.
	exportFeatures(formatId, featureIds, {options = {}, pointcloudName = null, label = null, epsg = null, projection = null, exportDate = null} = {}){
		const viewer = this.viewer;
		return runExport(ExportRegistry, {
			formatId,
			featureIds,
			options,
			context: {
				featureManager: this.featureManager,
				pointcloudName: pointcloudName || (viewer.getPointcloudName ? viewer.getPointcloudName() : null),
				label,
				epsg,
				projection: projection || (viewer.getProjection ? viewer.getProjection() : null),
				exportDate: exportDate || new Date(),
			},
		});
	}

	zoomToFeature(featureId){
		zoomToFeatureInViewer(this, featureId);
	}

	getHelpModel(){
		return {
			categories: HELP_CATEGORIES,
			articles: HELP_ARTICLES,
			getArticle,
			articlesInCategory,
			categoryLabel,
			search: searchArticles,
		};
	}

	mountHelpPanel(container){
		return mountDigitizationHelpPanel(container);
	}

	// CSV-profil-editoren (kolonner, headernavne, datakilde pr. kolonne, konstant/fallback, separator, decimaltegn,
	// encoding/BOM, koordinatpræcision; opret/kopiér/omdøb/slet profiler) i en <div>, som host-UI'et leverer - bruges af
	// Gem registreringer-dialogens "Redigér profiler…". Profilerne ligger i localStorage (CsvProfileStore), samme lager som
	// eksporten læser, så ændringer slår igennem i profil-vælgeren, når dialogen genindlæser formaterne (getExportModel()).
	// Returnerer {unmount()}.
	mountCsvProfileEditor(container){
		return mountCsvProfileEditorPanel(container, this);
	}

	// Same pattern as mountHelpPanel() - now that SettingsPanel.js is plain
	// DOM (no jQuery), a host UI only needs to hand this a plain <div>.
	// Indstillinger som ren data til en host-UI, der selv tegner dem (Vue).
	// Samme skema og samme lager som mountSettingsPanel() - én kilde.
	// groups: [{id, label, settings: [{key, label, type, tooltip}]}]
	getSettingsModel(){
		return {
			groups: visibleSettingsGroups().map((g) => ({id: g.id, label: g.label, settings: settingsInGroup(g.id)})),
			get: (key) => settings.get(key),
			set: (key, value) => settings.set(key, value),
			// abonnér på ændringer (returnerer afmeld-funktion) - så en anden
			// UI-flade (fx Snap-til/fra i topbarens dropdown) kan holde sig
			// synkron med Egenskaber-dialogen uden egen parallel state
			onChange: (fn) => settings.onChange(fn),
			reset: () => settings.reset(),
		};
	}

	mountSettingsPanel(container){
		return mountDigitizationSettingsPanel(container, this.viewer);
	}

	// Id for en optagelse (se startInsertion): bliver til den færdige registreringens id
	_newCaptureId(){
		this._captureSeq = (this._captureSeq || 0) + 1;
		return `feature_${Date.now().toString(36)}_c${this._captureSeq}`;
	}

	// tool: {id, name, category, resultGeometryType, captureGeometryType?,
	//        maxPoints, postProcessCoordinates?, featureType?, customCapture?}
	startInsertion(tool){
		// Profil-finalisering (B1): mens en kandidat fra SAMME værktøj afventer profilen, startes ingen 2. capture med det
		// (re-arm sker først, når profilen har svaret). Et andet værktøj afbryder (rådgivende) de ventende finaliseringer.
		if(this._isFinalizingTool(tool)) return null;
		this._abortFinalizations();

		copyClickGuard.consumed = false;
		this._operationActive = false; // (gen)armeret = idle
		this._operationPointcloudId = undefined;

		this.dispatchEvent({type: "start_inserting_feature"});
		this.viewer.dispatchEvent({type: "cancel_insertions"});

		this.activeTool = tool;
		this.dispatchEvent({type: 'active_tool_changed', tool});

		// extension point for tools whose capture flow doesn't fit the
		// generic single-visual/maxPoints model (e.g. Midline: two helper
		// vectors, then a chain of dynamic connectors) - such a tool owns
		// its entire interaction and calls back into startInsertion()/
		// featureManager itself when it produces a feature
		if(tool.customCapture){
			tool.customCapture(this, tool);
			return;
		}

		this._showInstruction(tool.name, this._resolveInstruction(tool, 0));

		const domElement = this.viewer.renderer.domElement;
		const captureType = tool.captureGeometryType || tool.resultGeometryType;
		const visual = new FeatureVisual(captureType);
		visual.postProcessCoordinates = tool.postProcessCoordinates || null;
		visual.vertexRoleNames = tool.vertexRoleNames || null;
		visual.setInfo(tool.name, "");
		if(tool.showComputedPoint && tool.postProcessCoordinates){
			visual.enableComputedPoint(tool.computedPointName || t("Beregnet punkt"), tool.postProcessCoordinates);
		}
		// Målinger tools (Mål afstand/areal) opt-in: no per-vertex coordinate
		// labels, a length/area label per segment, and a live-recomputed
		// summary label - see FeatureVisual.js's enableSegmentLabels()/
		// enableSummaryLabel() for why these live there rather than being
		// reimplemented per tool. Declarative fields (like showComputedPoint
		// above), not customCapture, since the capture INTERACTION itself
		// (left-click adds a point, right-click finishes) is the same generic
		// flow every basic geometry tool already uses - only the visual's
		// label behaviour differs.
		if(tool.suppressVertexLabels) visual.setVertexLabelsSuppressed(true);
		if(tool.segmentLabelFormat) visual.enableSegmentLabels(tool.segmentLabelFormat);
		if(tool.summaryLabelFormat) visual.enableSummaryLabel(tool.summaryLabelFormat, tool.summaryLabelAnchor);
		if(tool.fillColor) visual.enableFill(tool.fillColor, tool.fillOpacity);
		this.scene.add(visual);

		// Host-API (frontendens window.connect-bro, HOST-API.md): hosten får punkterne LIVE, mens de lægges. Den færdige
		// registrering får SAMME id som optagelsen (feature.id = captureId i buildFeatureIfValid), så hosten kan følge ét objekt fra
		// første klik til færdig. Kun når det, der optages, er det, der registreres (ingen efterbehandling/hjælpegeometri) - ellers
		// kender hosten først resultatet, når registreringen er færdig (feature_created).
		const captureId = this._newCaptureId();
		const liveCapture = !tool.postProcessCoordinates && captureType === tool.resultGeometryType;
		const announceCapture = () => {
			this.dispatchEvent({
				type: "capture_changed",
				captureId,
				tool: tool.id,
				kind: tool.kind || "measurement",
				geometryType: captureType,
				coordinates: visual.rawCoordinates(),
			});
		};
		if(liveCapture) visual.addEventListener("geometry_changed", announceCapture);

		const maxPoints = tool.maxPoints || Infinity;
		let finished = false;

		const cleanup = () => {
			// efter optagelsen er visualen registreringens egen: dens senere ændringer er feature_changed, ikke capture_changed
			visual.removeEventListener("geometry_changed", announceCapture);
			domElement.removeEventListener('mouseup', insertionCallback, false);
			this.viewer.removeEventListener('cancel_insertions', onCancel);
			this.clearRubberBand();
		};

		const buildFeatureIfValid = () => {
			if(visual.points.length < visual.minVertices){
				this.scene.remove(visual);
				this.dispatchEvent({type: "capture_discarded", captureId});
				return;
			}

			const coordinates = tool.postProcessCoordinates
				? tool.postProcessCoordinates(visual.rawCoordinates())
				: visual.toCoordinates();

			const feature = createFeature({
				geometryType: tool.resultGeometryType,
				coordinates: coordinates,
				category: tool.category,
				tool: tool.id,
				featureType: tool.featureType || tool.name,
				name: `${tool.name} ${this.featureManager.features.length + 1}`,
			});
			feature.id = captureId;

			visual.setInfo(feature.properties.name, feature.properties.description);
			// undefined (for kort), kandidaterne (synkron pass-through) eller en Promise (profil-finalisering)
			return this.commitCandidates([{feature, visual}], {tool});
		};

		const finishAndRestart = () => {
			if(finished) return;
			finished = true;
			cleanup();
			const outcome = buildFeatureIfValid();

			// stay active: immediately start capturing the next feature with
			// the same tool, unless it was stopped/switched in the meantime
			const rearm = (result) => {
				if(result && result.aborted) return;
				if(this.activeTool === tool){
					this.startInsertion(tool);
				}
			};

			if(outcome && typeof outcome.then === "function"){
				// FINALIZING: kandidaten afventer profilen. Værktøjet forbliver aktivt men re-armeres først, når profilen
				// har svaret og commit/kassér er sket; en abort (ESC/værktøjsskift/stop) re-armerer ikke.
				this._markOperationStarted();
				outcome.then(rearm, (error) => console.error("Registreringsprofil: finalisering fejlede", error));
				return;
			}

			rearm();
		};

		const onCancel = (e) => {
			if(finished) return;
			finished = true;
			cleanup();

			if(e && e.discard){
				// ESC: hard cancel - never commit a feature from this capture,
				// even if it's already a technically-valid Line/Polygon
				this.scene.remove(visual);
				this.dispatchEvent({type: "capture_discarded", captureId});
			}else{
				// a different tool took over (or capture was explicitly
				// stopped): keep whatever was already captured, but do not
				// restart
				buildFeatureIfValid();
			}
		};

		const insertionCallback = (e) => {
			if(finished || this.isPaused() || this._consumeCopyClickGuard()) return;

			if(e.button === THREE.MOUSE.LEFT){
				const location = this._pickPlacementPosition(e);

				if(location){
					visual.appendAtEnd(location);
					this._markOperationStarted();
					this._showInstruction(tool.name, this._resolveInstruction(tool, visual.points.length));
					this.syncRubberBandToVisual(visual, maxPoints);
				}

				if(visual.points.length >= maxPoints){
					finishAndRestart();
				}
			}else if(e.button === THREE.MOUSE.RIGHT){
				// finishing click - never places a vertex itself
				finishAndRestart();
			}
		};

		domElement.addEventListener('mouseup', insertionCallback, false);
		this.viewer.addEventListener('cancel_insertions', onCancel);

		return visual;
	}

	// Perf (task: "punktsky-navigationen foeles hakkende"): a fresh,
	// uncached AABB-derived bounding sphere from `points` (world positions -
	// FeatureVisual itself is never transformed, see updateDirectionIndicators'
	// own direct use of `this.points[i].position`). Deliberately NOT cached
	// on the visual: caching would need correct invalidation on every one of
	// the many code paths that mutate `points` (drag/snap/split/merge/trim/
	// insert/delete/...), and a missed invalidation would silently cull a
	// feature that should still be updating - recomputing fresh each frame
	// is cheap (one pass of comparisons, no trig) and can never be wrong.
	// Writes into `outSphere` (a caller-owned, reused-across-the-frame
	// THREE.Sphere) instead of allocating - this runs once per feature per
	// frame, so a fresh Sphere/Vector3 per call would reintroduce the exact
	// per-frame allocation cost this change exists to remove. Returns false
	// (out-sphere left untouched, caller then always updates) for <2 points
	// - matches every other method here treating that as "still being
	// placed".
	_computeFeatureBoundingSphere(points, outSphere){
		if(points.length < 2) return false;

		let minX = Infinity, minY = Infinity, minZ = Infinity;
		let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
		for(const p of points){
			const {x, y, z} = p.position;
			if(x < minX) minX = x; if(x > maxX) maxX = x;
			if(y < minY) minY = y; if(y > maxY) maxY = y;
			if(z < minZ) minZ = z; if(z > maxZ) maxZ = z;
		}
		outSphere.center.set((minX + maxX) / 2, (minY + maxY) / 2, (minZ + maxZ) / 2);
		const dx = maxX - minX, dy = maxY - minY, dz = maxZ - minZ;
		outSphere.radius = Math.sqrt(dx * dx + dy * dy + dz * dz) / 2;

		return true;
	}

	update(){
		const camera = this.viewer.scene.getActiveCamera();
		const renderAreaSize = this.renderer.getSize(this._sizeScratch || (this._sizeScratch = new THREE.Vector2()));
		const clientWidth = renderAreaSize.width;
		const clientHeight = renderAreaSize.height;

		// Labels (prioritet, zoomniveau, placering, overlap - labels/LabelManager.js): layoutet køres kun ved ændringer; true betyder, at
		// labelskala og skærm-forskydning skal opdateres i denne frame (kamera eller labels ændret). Ellers springes label-arbejdet over.
		const labelManager = this.labelManager;
		const frameNow = performance.now();
		this._updateHover(frameNow);
		const labelPass = labelManager.beginFrame(camera, clientWidth, clientHeight, frameNow);

		this.light.position.copy(camera.position);
		this.cursorTracker.frame();
		this.cursorMarker.refreshScale(camera, clientWidth, clientHeight);
		this.rubberBand.refreshResolution(clientWidth, clientHeight);

		// Perf: skip the expensive per-feature screen-space work below for
		// FeatureVisuals entirely outside the current view - see
		// _computeFeatureBoundingSphere()'s own comment on why this is safe
		// (all five gated methods are pure visual output, nothing else reads
		// them). A selected or multi-selected feature is NEVER culled, even
		// if its bounding sphere is out of frustum - selection state must
		// stay visually correct regardless of camera framing.
		// (objekterne genbruges frame efter frame - update() kører hver frame)
		const frustum = this._cullFrustum || (this._cullFrustum = new THREE.Frustum());
		const cullMatrix = this._cullMatrix || (this._cullMatrix = new THREE.Matrix4());
		frustum.setFromProjectionMatrix(cullMatrix.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse));
		const cullSphere = this._cullSphere || (this._cullSphere = new THREE.Sphere());
		let forceUpdateVisuals = null;
		if(this.multiSelection){
			forceUpdateVisuals = this._forceUpdateVisuals || (this._forceUpdateVisuals = new Set());
			forceUpdateVisuals.clear();
			for(const entry of this.multiSelection.entries) forceUpdateVisuals.add(entry.visual);
		}

		// Which FeatureVisuals are actual registered features (as opposed to a
		// tool's in-progress construction/helper geometry, which is a
		// FeatureVisual too but never enters `this.visuals`). Built once per
		// frame; used to gate direction indicators.
		const registeredVisuals = this._registeredVisuals || (this._registeredVisuals = new Set());
		registeredVisuals.clear();
		for(const visual of this.visuals.values()) registeredVisuals.add(visual);

		// scale every FeatureVisual currently in the scene, not just ones
		// already registered as finished Features - a tool's in-progress
		// construction geometry (e.g. Midline's Vector 1/Vector 2 while
		// still being digitized) is a FeatureVisual too and must keep its
		// on-screen size constant just the same, per the shared
		// screen-space-scaling requirement (see ScreenSpaceScale.js)
		for(const child of this.scene.children){
			if(child instanceof FeatureVisual){
				child.updateResolution(clientWidth, clientHeight);

				if(!child.selected && !(forceUpdateVisuals && forceUpdateVisuals.has(child))){
					const hasBounds = this._computeFeatureBoundingSphere(child.points, cullSphere);
					if(hasBounds && !frustum.intersectsSphere(cullSphere)){
						if(labelPass) labelManager.hideCulled(child);
						continue;
					}
				}

				child.updateSphereScale(camera, clientWidth, clientHeight);
				if(labelPass){
					child.updateLabelScale(camera, clientWidth, clientHeight, labelManager);
					// must run after updateLabelScale() - see its own comment
					child.updateCopyIcons(camera, clientWidth, clientHeight);
				}
				// suppress the insert-handle reveal while Ctrl-navigation is
				// active, same as every capture/preview handler
				child.updateInsertHandles(camera, this.isPaused() ? null : this.mouse, clientWidth, clientHeight);

				// Direction chevrons only for a registered, OPEN LineString
				// feature - i.e. exactly the geometry whose whole-object menu
				// offers "Vend retning" (see EditingCapabilities: a Polygon is
				// a closed ring with canReverse=false; a Rør is not a
				// FeatureVisual; construction/helper lines are never
				// registered). `null` camera => the visual hides all of them.
				const showDirection = registeredVisuals.has(child)
					&& child.geometryType === "LineString"
					&& !child.closed;
				child.updateDirectionIndicators(showDirection ? camera : null, clientWidth, clientHeight);
				// generic hook for a tool-attached decorative child object
				// (see FeatureVisual.setLabelsVisible()'s own comment on the
				// same userData.auxiliaryVisual convention) - e.g. Mål
				// højde's HeightAuxiliaryVisual
				if(child.userData.auxiliaryVisual){
					child.userData.auxiliaryVisual.update(camera, clientWidth, clientHeight);
				}
			}else if(child instanceof PipeVisual){
				child.updateResolution(clientWidth, clientHeight);
				// positions the copy-icon off the label, so must run after
				// the label's own scale is set - see updateScale()'s comment
				child.updateScale(camera, clientWidth, clientHeight, labelManager);
			}
		}

		// forbindelseslinjer følger labelens aktuelle forskydning, så de opdateres efter alle labels
		if(labelPass) labelManager.endFrame();
	}

	render(){
		this.viewer.renderer.render(this.scene, this.viewer.scene.getActiveCamera());
	}
};
